import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  AtSign,
  Building2,
  CheckCircle2,
  ChevronDown,
  Clock3,
  FileText,
  Globe2,
  Loader2,
  LockKeyhole,
  Mail,
  Plus,
  Save,
  Search,
  Send,
  ShieldCheck,
  Trash2,
  UserRound,
  X,
} from "lucide-react";

import TitleButtons from "../../../../../components/TitleButtons";
import { useTheme } from "../../../../../context/ThemeContext";
import { useAuth } from "../../../../../context/auth-context";

type SectionKey =
  | "visao-geral"
  | "pendencias"
  | "aguardando-validacao"
  | "vencidos"
  | "atualizados"
  | "clientes-portais";

type TipoCanalEnvio = "portal" | "email";

type StatusCadastral =
  | "regular"
  | "pendente_documental"
  | "documento_vencido"
  | "aguardando_validacao"
  | "bloqueado"
  | "nao_cadastrado"
  | "nao_avaliado";

type StatusDocumentoPortal =
  | "atualizado"
  | "pendente_anexo"
  | "renovacao_pendente"
  | "vencido"
  | "aguardando_validacao"
  | "nao_exigido"
  | "nao_avaliado";

type DocumentoExigidoPortal = {
  id: number;
  documentoPortalId?: number | null;
  documentoNome: string;
  categoria: string;
  orgaoEmissor: string;
  status: StatusDocumentoPortal;
  validade: string | null;
  arquivoId?: number | null;
  arquivoNome: string | null;
  atualizadoEm: string | null;
  responsavel: string;
  observacao?: string | null;
};

type PortalCliente = {
  id: number;
  tipoCanal: TipoCanalEnvio;
  clienteNome: string;
  clienteRazaoSocial: string;
  clienteCnpj: string;
  codigoInterno: string;

  portalNome: string;
  portalUrl: string;
  usuarioReferencia: string;

  emailsDestino: string;
  assuntoPadraoEmail: string;
  procedimentoEnvio: string;

  areaResponsavel: string;
  responsavelNome: string;
  statusCadastral: StatusCadastral;
  ultimoAcesso: string | null;
  ultimaAtualizacao: string | null;
  ultimoEnvioEmail: string | null;

  documentosExigidos: number;
  documentosAtualizados: number;
  documentosPendentes: number;
  documentosVencidos: number;
  documentosAguardandoValidacao: number;
  documentosRenovacaoPendente: number;

  observacao: string | null;
  ativo: boolean;
  documentos: DocumentoExigidoPortal[];
};

type DocumentoCatalogo = {
  id: number;
  nome: string;
  categoria: string;
  orgaoEmissor: string;
  responsavelNome: string;
  statusValidade: string;
};

type CanalFilterState = {
  cliente: string;
  canal: string;
  statusCadastral: string;
  responsavel: string;
  pendencia: string;
  tipoCanal: string;
};

type RegisterCanalForm = {
  tipoCanal: TipoCanalEnvio;
  clienteNome: string;
  clienteRazaoSocial: string;
  clienteCnpj: string;
  codigoInterno: string;

  portalNome: string;
  portalUrl: string;
  usuarioReferencia: string;

  emailsDestino: string;
  assuntoPadraoEmail: string;
  procedimentoEnvio: string;

  areaResponsavel: string;
  responsavelNome: string;
  statusCadastral: StatusCadastral;
  observacao: string;
  documentosExigidosIds: number[];
};

type ApiPortaisClientesResponse = {
  ok?: boolean;
  items?: any[];
  erro?: string;
};

type ApiPostPortalClienteResponse = {
  ok?: boolean;
  criado?: boolean;
  atualizado?: boolean;
  documentosVinculados?: number;
  portal?: any;
  item?: any;
  erro?: string;
};

type ApiPortalClienteDetalheResponse = {
  ok?: boolean;
  portal?: any;
  item?: any;
  items?: any[];
  erro?: string;
};

type ApiDocumentosResponse = {
  ok?: boolean;
  items?: any[];
  documentos?: any[];
  rows?: any[];
  data?: any[];
  erro?: string;
};

type GovernancaPortaisClientesLayoutProps = {
  section?: SectionKey;
};

const API_BASE = "https://api.logvi-havox.com";
const API_PORTAIS_CLIENTES_URL = `${API_BASE}/template/governanca/portais-clientes`;
const API_DOCUMENTOS_URL = `${API_BASE}/template/governanca/documentos`;
const API_DOCUMENTOS_PORTAIS_URL = `${API_BASE}/template/governanca/documentos/portais`;

const BASE_PATH = "/governanca-qualidade-e-compliance/portais-de-clientes";
const DEPARTMENT_COLOR = "#2f3f59";
const HEADER_HEIGHT = 52;
const LEFTBAR_WIDTH = 64;
const MOBILE_BREAKPOINT = 900;

const sections: Array<{
  key: SectionKey;
  label: string;
  path: string;
  icon: React.ReactNode;
}> = [
  {
    key: "visao-geral",
    label: "Visão geral",
    path: `${BASE_PATH}/visao-geral`,
    icon: <ShieldCheck size={14} />,
  },
  {
    key: "pendencias",
    label: "Pendências",
    path: `${BASE_PATH}/pendencias`,
    icon: <AlertTriangle size={14} />,
  },
  {
    key: "aguardando-validacao",
    label: "Aguardando validação",
    path: `${BASE_PATH}/aguardando-validacao`,
    icon: <Clock3 size={14} />,
  },
  {
    key: "vencidos",
    label: "Vencidos",
    path: `${BASE_PATH}/vencidos`,
    icon: <FileText size={14} />,
  },
  {
    key: "atualizados",
    label: "Atualizados",
    path: `${BASE_PATH}/atualizados`,
    icon: <CheckCircle2 size={14} />,
  },
  {
    key: "clientes-portais",
    label: "Clientes / Canais",
    path: `${BASE_PATH}/clientes-portais`,
    icon: <Globe2 size={14} />,
  },
];

const initialFilters: CanalFilterState = {
  cliente: "",
  canal: "",
  statusCadastral: "",
  responsavel: "",
  pendencia: "",
  tipoCanal: "",
};

const initialRegisterCanalForm: RegisterCanalForm = {
  tipoCanal: "portal",
  clienteNome: "",
  clienteRazaoSocial: "",
  clienteCnpj: "",
  codigoInterno: "",

  portalNome: "",
  portalUrl: "",
  usuarioReferencia: "",

  emailsDestino: "",
  assuntoPadraoEmail: "",
  procedimentoEnvio: "",

  areaResponsavel: "",
  responsavelNome: "",
  statusCadastral: "nao_avaliado",
  observacao: "",
  documentosExigidosIds: [],
};

function normalizeTextSearch(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function useViewportWidth() {
  const [width, setWidth] = useState<number>(() =>
    typeof window === "undefined" ? 1280 : window.innerWidth
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => {
      setWidth(window.innerWidth);
    };

    update();

    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("resize", update);
    };
  }, []);

  return width;
}

function toNumber(value: unknown, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toNullableString(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  return str ? str : null;
}

function getEmpresaIdFromSessao(sessao: any): number | null {
  const value =
    sessao?.empresa_id ??
    sessao?.empresaId ??
    sessao?.empresa_atual_id ??
    sessao?.empresaSelecionadaId ??
    sessao?.empresa?.id;

  if (value === undefined || value === null || value === "") return null;

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getModuloIdFromSessao(sessao: any): number | null {
  return getModuloIdByKindFromSessao(sessao, "portais");
}

function getModuloDocumentosIdFromSessao(sessao: any): number | null {
  return getModuloIdByKindFromSessao(sessao, "documentos");
}

function getModuloIdByKindFromSessao(
  sessao: any,
  kind: "portais" | "documentos"
): number | null {
  const direct =
    kind === "portais"
      ? sessao?.modulo_id ??
        sessao?.moduloId ??
        sessao?.modulo_atual_id ??
        sessao?.moduloSelecionadoId ??
        sessao?.modulo?.id
      : null;

  if (direct !== undefined && direct !== null && direct !== "") {
    const n = Number(direct);
    if (Number.isFinite(n)) return n;
  }

  const isGovernancaDepartment = (dep: any) => {
    const full = [
      dep?.slug,
      dep?.nome,
      dep?.routeKey,
      dep?.route_key,
      dep?.codigo,
    ]
      .map(normalizeTextSearch)
      .join(" ");

    return (
      full.includes("governanca") ||
      full.includes("governança") ||
      full.includes("compliance")
    );
  };

  const matchesKind = (mod: any) => {
    const id = mod?.id;

    if (id === undefined || id === null || id === "") return false;

    const full = [
      mod?.codigo,
      mod?.label,
      mod?.nome,
      mod?.routeKey,
      mod?.route_key,
      mod?.actionKey,
      mod?.action_key,
    ]
      .map(normalizeTextSearch)
      .join(" ");

    if (kind === "portais") {
      return (
        full.includes("portais") ||
        full.includes("portal") ||
        full.includes("clientes") ||
        full.includes("canais")
      );
    }

    return (
      full.includes("documentos") ||
      full.includes("documento") ||
      full.includes("certidoes") ||
      full.includes("certidões")
    );
  };

  const extractFromDepartments = (departamentos: any[]): number | null => {
    for (const dep of departamentos) {
      if (!isGovernancaDepartment(dep)) continue;

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];
      const modulo = modulos.find((mod: any) => matchesKind(mod));

      const id = modulo?.id;
      if (id !== undefined && id !== null && id !== "") {
        const n = Number(id);
        if (Number.isFinite(n)) return n;
      }
    }

    return null;
  };

  const departamentosDiretos = Array.isArray(sessao?.departamentos_acessiveis)
    ? sessao.departamentos_acessiveis
    : [];

  const fromDepartamentosDiretos = extractFromDepartments(departamentosDiretos);
  if (fromDepartamentosDiretos) return fromDepartamentosDiretos;

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosComModulos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  if (enderecoSelecionadoId) {
    const enderecoSelecionado = enderecosComModulos.find(
      (end: any) => String(end?.id ?? "") === enderecoSelecionadoId
    );

    const departamentosEndereco = Array.isArray(
      enderecoSelecionado?.departamentos_acessiveis
    )
      ? enderecoSelecionado.departamentos_acessiveis
      : [];

    const fromEnderecoSelecionado = extractFromDepartments(
      departamentosEndereco
    );
    if (fromEnderecoSelecionado) return fromEnderecoSelecionado;
  }

  for (const endereco of enderecosComModulos) {
    const departamentosEndereco = Array.isArray(
      endereco?.departamentos_acessiveis
    )
      ? endereco.departamentos_acessiveis
      : [];

    const fromEndereco = extractFromDepartments(departamentosEndereco);
    if (fromEndereco) return fromEndereco;
  }

  return null;
}

function getAuthToken(auth: any): string | null {
  return (
    toNullableString(auth?.token) ||
    toNullableString(auth?.accessToken) ||
    toNullableString(auth?.access_token) ||
    toNullableString(auth?.jwt) ||
    toNullableString(auth?.sessao?.token)
  );
}

function normalizeTipoCanal(value: unknown): TipoCanalEnvio {
  const tipo = normalizeTextSearch(value);
  if (tipo === "email" || tipo === "e-mail" || tipo === "mail") return "email";
  return "portal";
}

function normalizeStatusDocumento(value: unknown): StatusDocumentoPortal {
  const status = normalizeTextSearch(value).replace(/-/g, "_");

  if (
    status === "atualizado" ||
    status === "regular" ||
    status === "vigente" ||
    status === "valido" ||
    status === "válido"
  ) {
    return "atualizado";
  }

  if (
    status === "vencido" ||
    status === "documento_vencido" ||
    status === "expirado"
  ) {
    return "vencido";
  }

  if (
    status === "pendente_anexo" ||
    status === "sem_anexo" ||
    status === "pendente" ||
    status === "pendente_documental"
  ) {
    return "pendente_anexo";
  }

  if (
    status === "renovacao_pendente" ||
    status === "renovação_pendente" ||
    status === "a_renovar" ||
    status === "a_vencer"
  ) {
    return "renovacao_pendente";
  }

  if (
    status === "aguardando_validacao" ||
    status === "aguardando_validação" ||
    status === "em_validacao" ||
    status === "em_validação"
  ) {
    return "aguardando_validacao";
  }

  if (status === "nao_exigido" || status === "não_exigido") {
    return "nao_exigido";
  }

  return "nao_avaliado";
}

function normalizeDocumentoPortalFromApi(
  item: any
): DocumentoExigidoPortal | null {
  const id = toNumber(
    item?.id ??
      item?.documentoId ??
      item?.documento_id ??
      item?.documento?.id ??
      item?.documento_portal_id ??
      null
  );

  if (!id) return null;

  const status = normalizeStatusDocumento(
    item?.status ??
      item?.statusOperacional ??
      item?.status_operacional ??
      item?.statusValidade ??
      item?.status_validade ??
      item?.documento?.statusValidade ??
      item?.documento?.status_validade
  );

  return {
    id,
    documentoPortalId:
      item?.documentoPortalId !== undefined ||
      item?.documento_portal_id !== undefined
        ? toNumber(item?.documentoPortalId ?? item?.documento_portal_id)
        : null,
    documentoNome:
      toNullableString(item?.documentoNome) ||
      toNullableString(item?.documento_nome) ||
      toNullableString(item?.nome) ||
      toNullableString(item?.documento?.nome) ||
      `Documento ${id}`,
    categoria:
      toNullableString(item?.categoria) ||
      toNullableString(item?.documento?.categoria) ||
      "Sem categoria",
    orgaoEmissor:
      toNullableString(item?.orgaoEmissor) ||
      toNullableString(item?.orgao_emissor) ||
      toNullableString(item?.documento?.orgaoEmissor) ||
      toNullableString(item?.documento?.orgao_emissor) ||
      "Não informado",
    status,
    validade:
      toNullableString(item?.validade) ||
      toNullableString(item?.dataValidade) ||
      toNullableString(item?.data_validade) ||
      toNullableString(item?.documento?.dataValidade) ||
      toNullableString(item?.documento?.data_validade),
    arquivoId:
      item?.arquivoId !== undefined || item?.arquivo_id !== undefined
        ? toNumber(item?.arquivoId ?? item?.arquivo_id)
        : null,
    arquivoNome:
      toNullableString(item?.arquivoNome) ||
      toNullableString(item?.arquivo_nome) ||
      toNullableString(item?.originalName) ||
      toNullableString(item?.original_name) ||
      toNullableString(item?.documento?.arquivoNome) ||
      toNullableString(item?.documento?.arquivo_nome),
    atualizadoEm:
      toNullableString(item?.atualizadoEm) ||
      toNullableString(item?.atualizado_em) ||
      toNullableString(item?.dataUpload) ||
      toNullableString(item?.data_upload),
    responsavel:
      toNullableString(item?.responsavel) ||
      toNullableString(item?.responsavelNome) ||
      toNullableString(item?.responsavel_nome) ||
      toNullableString(item?.documento?.responsavelNome) ||
      toNullableString(item?.documento?.responsavel_nome) ||
      "Não informado",
    observacao:
      toNullableString(item?.observacao) ||
      toNullableString(item?.observacaoPortal) ||
      toNullableString(item?.observacao_portal) ||
      null,
  };
}

function normalizePortalFromApi(item: any): PortalCliente {
  const tipoCanal = normalizeTipoCanal(
    item?.tipoCanal ?? item?.tipo_canal ?? item?.canalEnvio ?? item?.canal_envio
  );

  const emailsDestino =
    toNullableString(item?.emailsDestino) ||
    toNullableString(item?.emails_destino) ||
    toNullableString(item?.emailDestino) ||
    toNullableString(item?.email_destino) ||
    toNullableString(item?.portalEmail) ||
    "";

  const portalNome =
    toNullableString(item?.portalNome) ||
    toNullableString(item?.portal_nome) ||
    (tipoCanal === "email" ? "Envio por e-mail" : "Portal sem nome");

  return {
    id: toNumber(item?.id),
    tipoCanal,
    clienteNome:
      toNullableString(item?.clienteNome) ||
      toNullableString(item?.cliente_nome) ||
      "Cliente sem nome",
    clienteRazaoSocial:
      toNullableString(item?.clienteRazaoSocial) ||
      toNullableString(item?.cliente_razao_social) ||
      toNullableString(item?.clienteNome) ||
      toNullableString(item?.cliente_nome) ||
      "Não informado",
    clienteCnpj:
      toNullableString(item?.clienteCnpj) ||
      toNullableString(item?.cliente_cnpj) ||
      "Não informado",
    codigoInterno:
      toNullableString(item?.codigoInterno) ||
      toNullableString(item?.codigo_interno) ||
      "",
    portalNome,
    portalUrl:
      toNullableString(item?.portalUrl) ||
      toNullableString(item?.portal_url) ||
      "",
    usuarioReferencia:
      toNullableString(item?.usuarioReferencia) ||
      toNullableString(item?.usuario_referencia) ||
      "",
    emailsDestino,
    assuntoPadraoEmail:
      toNullableString(item?.assuntoPadraoEmail) ||
      toNullableString(item?.assunto_padrao_email) ||
      "",
    procedimentoEnvio:
      toNullableString(item?.procedimentoEnvio) ||
      toNullableString(item?.procedimento_envio) ||
      toNullableString(item?.regraEnvio) ||
      toNullableString(item?.regra_envio) ||
      "",
    areaResponsavel:
      toNullableString(item?.areaResponsavel) ||
      toNullableString(item?.area_responsavel) ||
      "Governança",
    responsavelNome:
      toNullableString(item?.responsavelNome) ||
      toNullableString(item?.responsavel_nome) ||
      "Não informado",
    statusCadastral:
      item?.statusCadastral === "regular" ||
      item?.statusCadastral === "pendente_documental" ||
      item?.statusCadastral === "documento_vencido" ||
      item?.statusCadastral === "aguardando_validacao" ||
      item?.statusCadastral === "bloqueado" ||
      item?.statusCadastral === "nao_cadastrado" ||
      item?.statusCadastral === "nao_avaliado"
        ? item.statusCadastral
        : item?.status_cadastral === "regular" ||
          item?.status_cadastral === "pendente_documental" ||
          item?.status_cadastral === "documento_vencido" ||
          item?.status_cadastral === "aguardando_validacao" ||
          item?.status_cadastral === "bloqueado" ||
          item?.status_cadastral === "nao_cadastrado" ||
          item?.status_cadastral === "nao_avaliado"
        ? item.status_cadastral
        : "nao_avaliado",
    ultimoAcesso:
      toNullableString(item?.ultimoAcesso) ||
      toNullableString(item?.ultimo_acesso),
    ultimaAtualizacao:
      toNullableString(item?.ultimaAtualizacao) ||
      toNullableString(item?.ultima_atualizacao),
    ultimoEnvioEmail:
      toNullableString(item?.ultimoEnvioEmail) ||
      toNullableString(item?.ultimo_envio_email),
    documentosExigidos: toNumber(
      item?.documentosExigidos ?? item?.documentos_exigidos
    ),
    documentosAtualizados: toNumber(
      item?.documentosAtualizados ?? item?.documentos_atualizados
    ),
    documentosPendentes: toNumber(
      item?.documentosPendentes ?? item?.documentos_pendentes
    ),
    documentosVencidos: toNumber(
      item?.documentosVencidos ?? item?.documentos_vencidos
    ),
    documentosAguardandoValidacao: toNumber(
      item?.documentosAguardandoValidacao ??
        item?.documentos_aguardando_validacao
    ),
    documentosRenovacaoPendente: toNumber(
      item?.documentosRenovacaoPendente ?? item?.documentos_renovacao_pendente
    ),
    observacao: toNullableString(item?.observacao),
    ativo: item?.ativo !== false,
    documentos: Array.isArray(item?.documentos)
      ? (item.documentos
          .map(normalizeDocumentoPortalFromApi)
          .filter(Boolean) as DocumentoExigidoPortal[])
      : [],
  };
}

function normalizeDocumentoFromApi(item: any): DocumentoCatalogo | null {
  const id = toNumber(
    item?.id ??
      item?.documentoId ??
      item?.documento_id ??
      item?.documento?.id ??
      null
  );

  if (!id) return null;

  return {
    id,
    nome:
      toNullableString(item?.nome) ||
      toNullableString(item?.documentoNome) ||
      toNullableString(item?.documento_nome) ||
      toNullableString(item?.titulo) ||
      `Documento ${id}`,
    categoria:
      toNullableString(item?.categoria) ||
      toNullableString(item?.categoriaNome) ||
      "Sem categoria",
    orgaoEmissor:
      toNullableString(item?.orgaoEmissor) ||
      toNullableString(item?.orgao_emissor) ||
      "Não informado",
    responsavelNome:
      toNullableString(item?.responsavelNome) ||
      toNullableString(item?.responsavel_nome) ||
      toNullableString(item?.responsavel) ||
      "Não informado",
    statusValidade:
      toNullableString(item?.statusValidade) ||
      toNullableString(item?.status_validade) ||
      "nao_avaliado",
  };
}

function deriveStatusCadastralFromDocumentos(
  selectedDocumentos: DocumentoCatalogo[],
  fallback: StatusCadastral
): StatusCadastral {
  if (fallback === "bloqueado" || fallback === "nao_cadastrado") {
    return fallback;
  }

  const statuses = selectedDocumentos.map((doc) =>
    normalizeStatusDocumento(doc.statusValidade)
  );

  if (statuses.includes("vencido")) return "documento_vencido";

  if (
    statuses.includes("pendente_anexo") ||
    statuses.includes("renovacao_pendente")
  ) {
    return "pendente_documental";
  }

  if (statuses.includes("aguardando_validacao")) {
    return "aguardando_validacao";
  }

  if (statuses.length === 0 || statuses.includes("nao_avaliado")) {
    return "nao_avaliado";
  }

  return "regular";
}

function getDocumentoStatusLabel(status: unknown) {
  const normalized = normalizeStatusDocumento(status);

  switch (normalized) {
    case "atualizado":
      return "Atualizado";
    case "pendente_anexo":
      return "Pendente de anexo";
    case "renovacao_pendente":
      return "Renovação pendente";
    case "vencido":
      return "Vencido";
    case "aguardando_validacao":
      return "Aguardando validação";
    case "nao_exigido":
      return "Não exigido";
    case "nao_avaliado":
    default:
      return "Não avaliado";
  }
}

function getDocumentoStatusVisual(status: unknown) {
  const normalized = normalizeStatusDocumento(status);

  switch (normalized) {
    case "atualizado":
      return { background: "#ecfdf3", color: "#166534", border: "#bbf7d0" };
    case "vencido":
      return { background: "#fef2f2", color: "#991b1b", border: "#fecaca" };
    case "pendente_anexo":
    case "renovacao_pendente":
      return { background: "#fff7ed", color: "#9a3412", border: "#fed7aa" };
    case "aguardando_validacao":
      return { background: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" };
    case "nao_exigido":
    case "nao_avaliado":
    default:
      return { background: "#f8fafc", color: "#475569", border: "#cbd5e1" };
  }
}

function isPortalStatusRestritivoPorDocumento(status: StatusCadastral) {
  return (
    status === "documento_vencido" ||
    status === "pendente_documental" ||
    status === "aguardando_validacao" ||
    status === "nao_avaliado"
  );
}

function getDocumentosArrayFromApi(data: ApiDocumentosResponse | null): any[] {
  if (!data) return [];

  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.documentos)) return data.documentos;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.data)) return data.data;

  return [];
}

function getSectionFromPath(pathname: string): SectionKey {
  const path = pathname.toLowerCase();

  if (path.includes("pendencias")) return "pendencias";
  if (path.includes("aguardando-validacao")) return "aguardando-validacao";
  if (path.includes("vencidos")) return "vencidos";
  if (path.includes("atualizados")) return "atualizados";
  if (path.includes("clientes-portais")) return "clientes-portais";

  return "visao-geral";
}

function plural(value: number, singular: string, pluralText: string) {
  return value === 1 ? singular : pluralText;
}

function getStatusCadastralLabel(status: StatusCadastral) {
  switch (status) {
    case "regular":
      return "Regular";
    case "pendente_documental":
      return "Pendente documental";
    case "documento_vencido":
      return "Documento vencido";
    case "aguardando_validacao":
      return "Aguardando validação";
    case "bloqueado":
      return "Bloqueado";
    case "nao_cadastrado":
      return "Não cadastrado";
    case "nao_avaliado":
      return "Não avaliado";
    default:
      return "Indefinido";
  }
}

function getStatusCadastralVisual(status: StatusCadastral) {
  switch (status) {
    case "regular":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };
    case "pendente_documental":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <AlertTriangle size={12} />,
      };
    case "documento_vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <AlertTriangle size={12} />,
      };
    case "aguardando_validacao":
      return {
        background: "#f5f3ff",
        color: "#6d28d9",
        border: "#ddd6fe",
        icon: <Clock3 size={12} />,
      };
    case "bloqueado":
      return {
        background: "#fff1f2",
        color: "#9f1239",
        border: "#fecdd3",
        icon: <LockKeyhole size={12} />,
      };
    case "nao_cadastrado":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };
    case "nao_avaliado":
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getTipoCanalLabel(tipo: TipoCanalEnvio) {
  return tipo === "email" ? "Envio por e-mail" : "Portal do cliente";
}

function getCanalPrincipalLabel(item: PortalCliente) {
  if (item.tipoCanal === "email") {
    return item.emailsDestino || "E-mail não informado";
  }

  return item.portalNome || "Portal sem nome";
}

function getCanalSecundarioLabel(item: PortalCliente) {
  if (item.tipoCanal === "email") {
    return (
      item.assuntoPadraoEmail ||
      item.procedimentoEnvio ||
      "Procedimento de envio não informado"
    );
  }

  return item.portalUrl || item.usuarioReferencia || "Sem URL";
}

function hasPendencia(item: PortalCliente) {
  return (
    item.documentosPendentes > 0 ||
    item.documentosVencidos > 0 ||
    item.documentosAguardandoValidacao > 0 ||
    item.documentosRenovacaoPendente > 0 ||
    item.statusCadastral !== "regular"
  );
}

function getSectionTitle(section: SectionKey) {
  switch (section) {
    case "pendencias":
      return "Pendências documentais";
    case "aguardando-validacao":
      return "Aguardando validação";
    case "vencidos":
      return "Documentos vencidos";
    case "atualizados":
      return "Canais atualizados";
    case "clientes-portais":
      return "Clientes e canais de envio";
    case "visao-geral":
    default:
      return "Visão geral";
  }
}


function getDocumentosExigidosIdsFromPortal(item: PortalCliente | null) {
  if (!item || !Array.isArray(item.documentos)) return [];

  return Array.from(
    new Set(
      item.documentos
        .map((doc) => Number(doc.id))
        .filter((id) => Number.isFinite(id) && id > 0)
    )
  );
}

function portalToRegisterCanalForm(item: PortalCliente): RegisterCanalForm {
  return {
    tipoCanal: item.tipoCanal,
    clienteNome: item.clienteNome || "",
    clienteRazaoSocial: item.clienteRazaoSocial || "",
    clienteCnpj: item.clienteCnpj || "",
    codigoInterno: item.codigoInterno || "",

    portalNome: item.portalNome || "",
    portalUrl: item.portalUrl || "",
    usuarioReferencia: item.usuarioReferencia || "",

    emailsDestino: item.emailsDestino || "",
    assuntoPadraoEmail: item.assuntoPadraoEmail || "",
    procedimentoEnvio: item.procedimentoEnvio || "",

    areaResponsavel: item.areaResponsavel || "Governança",
    responsavelNome: item.responsavelNome || "",
    statusCadastral: item.statusCadastral || "nao_avaliado",
    observacao: item.observacao || "",
    documentosExigidosIds: getDocumentosExigidosIdsFromPortal(item),
  };
}

const Badge: React.FC<{
  children: React.ReactNode;
  background: string;
  color: string;
  border: string;
  icon?: React.ReactNode;
}> = ({ children, background, color, border, icon }) => {
  return (
    <span style={{ ...styles.badge, background, color, borderColor: border }}>
      {icon}
      {children}
    </span>
  );
};

const RegisterCanalOverlay: React.FC<{
  open: boolean;
  form: RegisterCanalForm;
  documentosCatalogo: DocumentoCatalogo[];
  documentosLoading: boolean;
  documentosError: string | null;
  departmentColor: string;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onChange: <K extends keyof RegisterCanalForm>(
    key: K,
    value: RegisterCanalForm[K]
  ) => void;
  onAddDocumento: (documentoId: number) => void;
  onRemoveDocumento: (documentoId: number) => void;
  onSubmit: () => void;
}> = ({
  open,
  form,
  documentosCatalogo,
  documentosLoading,
  documentosError,
  departmentColor,
  saving,
  error,
  onClose,
  onChange,
  onAddDocumento,
  onRemoveDocumento,
  onSubmit,
}) => {
  const viewportWidth = useViewportWidth();
  const isMobileViewport = viewportWidth < MOBILE_BREAKPOINT;

  const [documentDropdownOpen, setDocumentDropdownOpen] = useState(false);
  const [documentSearch, setDocumentSearch] = useState("");

  useEffect(() => {
    if (!open) {
      setDocumentDropdownOpen(false);
      setDocumentSearch("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, saving, onClose]);

  if (!open) return null;

  const selectedDocumentos = documentosCatalogo.filter((doc) =>
    form.documentosExigidosIds.includes(doc.id)
  );

  const statusCadastralCalculado = deriveStatusCadastralFromDocumentos(
    selectedDocumentos,
    form.statusCadastral
  );

  const hasDocumentoInvalido = isPortalStatusRestritivoPorDocumento(
    statusCadastralCalculado
  );

  const availableDocumentos = documentosCatalogo.filter(
    (doc) => !form.documentosExigidosIds.includes(doc.id)
  );

  const search = normalizeTextSearch(documentSearch);

  const filteredAvailableDocumentos = availableDocumentos.filter((doc) => {
    if (!search) return true;

    return [
      doc.nome,
      doc.categoria,
      doc.orgaoEmissor,
      doc.responsavelNome,
      doc.statusValidade,
    ].some((field) => normalizeTextSearch(field).includes(search));
  });

  const canalObrigatorioOk =
    form.tipoCanal === "portal"
      ? String(form.portalNome ?? "").trim().length > 0
      : String(form.emailsDestino ?? "").trim().length > 0;

  const canSubmit =
    String(form.clienteNome ?? "").trim().length > 0 &&
    canalObrigatorioOk &&
    !saving;

  const overlayBackdropStyle: React.CSSProperties = {
    ...styles.overlayBackdrop,
    top: isMobileViewport ? 0 : HEADER_HEIGHT,
    left: isMobileViewport ? 0 : LEFTBAR_WIDTH,
    zIndex: isMobileViewport ? 1400 : 1190,
  };

  const overlayPanelStyle: React.CSSProperties = {
    ...styles.overlayPanel,
    width: isMobileViewport
      ? "100vw"
      : `min(1040px, calc(100vw - ${LEFTBAR_WIDTH}px))`,
    height: isMobileViewport ? "100vh" : `calc(100vh - ${HEADER_HEIGHT}px)`,
  };

  return (
    <div
      style={overlayBackdropStyle}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) {
          onClose();
        }
      }}
    >
      <div
        style={overlayPanelStyle}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={styles.overlayHeader}>
          <div>
            <div style={styles.overlayEyebrow}>Governança documental</div>
            <h2 style={styles.overlayTitle}>Registrar canal do cliente</h2>
            <p style={styles.overlaySubtitle}>
              Use portal quando o cliente possuir ambiente próprio. Use e-mail
              quando os documentos forem enviados diretamente ao fiscal,
              comprador, solicitante ou outro responsável.
            </p>
          </div>

          <button
            type="button"
            style={styles.overlayCloseButton}
            onClick={onClose}
            disabled={saving}
          >
            <X size={17} />
          </button>
        </div>

        {error && (
          <div style={styles.overlayError}>
            <AlertTriangle size={15} />
            <span>{error}</span>
          </div>
        )}

        <div style={styles.formGrid}>
          <div style={styles.formSectionTitle}>
            <Building2 size={14} />
            <span>Cliente</span>
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Nome do cliente *</label>
            <input
              style={styles.formInput}
              value={form.clienteNome}
              onChange={(event) => onChange("clienteNome", event.target.value)}
              placeholder="Ex.: Bravante / Marlin Navegação"
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Código interno</label>
            <input
              style={styles.formInput}
              value={form.codigoInterno}
              onChange={(event) =>
                onChange("codigoInterno", event.target.value)
              }
              placeholder="Ex.: CLI-MARLIN"
              disabled={saving}
            />
          </div>

          <div style={styles.formFieldWide}>
            <label style={styles.formLabel}>Razão social</label>
            <input
              style={styles.formInput}
              value={form.clienteRazaoSocial}
              onChange={(event) =>
                onChange("clienteRazaoSocial", event.target.value)
              }
              placeholder="Ex.: MARLIN NAVEGAÇÃO S.A."
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>CNPJ / Identificação</label>
            <input
              style={styles.formInput}
              value={form.clienteCnpj}
              onChange={(event) => onChange("clienteCnpj", event.target.value)}
              placeholder="Ex.: 00.000.000/0000-00"
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Status cadastral</label>
            <select
              style={{
                ...styles.formInput,
                background: "#FFFFFF",
                color: hasDocumentoInvalido ? "#9a3412" : "#166534",
              }}
              value={statusCadastralCalculado}
              disabled
            >
              <option value="regular">Regular</option>
              <option value="pendente_documental">Pendente documental</option>
              <option value="documento_vencido">Documento vencido</option>
              <option value="aguardando_validacao">Aguardando validação</option>
              <option value="bloqueado">Bloqueado</option>
              <option value="nao_cadastrado">Não cadastrado</option>
              <option value="nao_avaliado">Não avaliado</option>
            </select>
            <span style={styles.formHelpText}>
              Status calculado automaticamente pelos documentos selecionados.
            </span>
          </div>

          <div style={styles.formSectionTitle}>
            <Send size={14} />
            <span>Canal de envio</span>
          </div>

          <div style={styles.formFieldWide}>
            <label style={styles.formLabel}>Tipo de canal *</label>
            <div style={styles.segmentedControl}>
              <button
                type="button"
                style={{
                  ...styles.segmentedButton,
                  ...(form.tipoCanal === "portal"
                    ? {
                        background: toRgba(departmentColor, 0.08),
                        color: departmentColor,
                        borderColor: departmentColor,
                      }
                    : {}),
                }}
                onClick={() => onChange("tipoCanal", "portal")}
                disabled={saving}
              >
                <Globe2 size={14} />
                Portal do cliente
              </button>

              <button
                type="button"
                style={{
                  ...styles.segmentedButton,
                  ...(form.tipoCanal === "email"
                    ? {
                        background: toRgba(departmentColor, 0.08),
                        color: departmentColor,
                        borderColor: departmentColor,
                      }
                    : {}),
                }}
                onClick={() => onChange("tipoCanal", "email")}
                disabled={saving}
              >
                <Mail size={14} />
                Envio por e-mail
              </button>
            </div>
          </div>

          {form.tipoCanal === "portal" ? (
            <>
              <div style={styles.formField}>
                <label style={styles.formLabel}>Nome do portal *</label>
                <input
                  style={styles.formInput}
                  value={form.portalNome}
                  onChange={(event) =>
                    onChange("portalNome", event.target.value)
                  }
                  placeholder="Ex.: Meritum / Ariba / Portal do Fornecedor"
                  disabled={saving}
                />
              </div>

              <div style={styles.formField}>
                <label style={styles.formLabel}>Usuário / referência</label>
                <input
                  style={styles.formInput}
                  value={form.usuarioReferencia}
                  onChange={(event) =>
                    onChange("usuarioReferencia", event.target.value)
                  }
                  placeholder="Ex.: service@havox.com.br"
                  disabled={saving}
                />
              </div>

              <div style={styles.formFieldWide}>
                <label style={styles.formLabel}>URL do portal</label>
                <input
                  style={styles.formInput}
                  value={form.portalUrl}
                  onChange={(event) =>
                    onChange("portalUrl", event.target.value)
                  }
                  placeholder="https://..."
                  disabled={saving}
                />
              </div>
            </>
          ) : (
            <>
              <div style={styles.formFieldWide}>
                <label style={styles.formLabel}>E-mails de destino *</label>
                <input
                  style={styles.formInput}
                  value={form.emailsDestino}
                  onChange={(event) =>
                    onChange("emailsDestino", event.target.value)
                  }
                  placeholder="Ex.: fiscal@cliente.com.br; solicitante@cliente.com.br"
                  disabled={saving}
                />
                <span style={styles.formHelpText}>
                  Separe múltiplos destinatários por vírgula ou ponto e vírgula.
                </span>
              </div>

              <div style={styles.formFieldWide}>
                <label style={styles.formLabel}>Assunto padrão do e-mail</label>
                <input
                  style={styles.formInput}
                  value={form.assuntoPadraoEmail}
                  onChange={(event) =>
                    onChange("assuntoPadraoEmail", event.target.value)
                  }
                  placeholder="Ex.: Documentos cadastrais / NFS-e / Relatórios de serviço"
                  disabled={saving}
                />
              </div>

              <div style={styles.formFieldWide}>
                <label style={styles.formLabel}>Procedimento de envio</label>
                <textarea
                  style={styles.formTextarea}
                  value={form.procedimentoEnvio}
                  onChange={(event) =>
                    onChange("procedimentoEnvio", event.target.value)
                  }
                  placeholder="Ex.: Enviar NFS-e para o fiscal e relatório de serviço para o solicitante. Copiar compras quando houver pedido de compra."
                  disabled={saving}
                />
              </div>
            </>
          )}

          <div style={styles.formSectionTitle}>
            <FileText size={14} />
            <span>Documentos exigidos</span>
          </div>

          <div style={styles.formFieldWide}>
            <label style={styles.formLabel}>Adicionar documento exigido</label>

            <div style={styles.docSelectShell}>
              <button
                type="button"
                style={styles.docSelectButton}
                onClick={() => setDocumentDropdownOpen((current) => !current)}
                disabled={saving || documentosLoading}
              >
                <span style={styles.docSelectButtonText}>
                  {documentosLoading
                    ? "Carregando documentos..."
                    : "Selecionar documento existente"}
                </span>

                {documentosLoading ? (
                  <Loader2 size={15} />
                ) : (
                  <ChevronDown size={15} />
                )}
              </button>

              {documentDropdownOpen && (
                <div style={styles.docDropdown}>
                  <div style={styles.docSearchBox}>
                    <Search size={14} />
                    <input
                      style={styles.docSearchInput}
                      value={documentSearch}
                      onChange={(event) =>
                        setDocumentSearch(event.target.value)
                      }
                      placeholder="Buscar por documento, categoria ou órgão..."
                      autoFocus
                    />
                  </div>

                  {documentosError && (
                    <div style={styles.docDropdownError}>
                      <AlertTriangle size={14} />
                      <span>{documentosError}</span>
                    </div>
                  )}

                  {!documentosError &&
                    !documentosLoading &&
                    filteredAvailableDocumentos.length === 0 && (
                      <div style={styles.docDropdownEmpty}>
                        Nenhum documento disponível para adicionar.
                      </div>
                    )}

                  {!documentosError &&
                    !documentosLoading &&
                    filteredAvailableDocumentos.map((doc) => (
                      <button
                        key={doc.id}
                        type="button"
                        style={styles.docOption}
                        onClick={() => {
                          onAddDocumento(doc.id);
                          setDocumentSearch("");
                          setDocumentDropdownOpen(false);
                        }}
                      >
                        <span style={styles.docOptionMain}>{doc.nome}</span>
                        <span style={styles.docOptionMeta}>
                          {doc.categoria} • {doc.orgaoEmissor}
                        </span>
                      </button>
                    ))}
                </div>
              )}
            </div>

            <div style={styles.selectedDocsListPanel}>
              <div style={styles.selectedDocsListHeader}>
                <div>
                  <strong style={styles.selectedDocsTitle}>
                    Documentos adicionados
                  </strong>
                  <span style={styles.selectedDocsHint}>
                    {selectedDocumentos.length === 0
                      ? "Nenhum documento exigido selecionado."
                      : `${selectedDocumentos.length} documento(s) exigido(s) selecionado(s).`}
                  </span>
                </div>
              </div>

              {selectedDocumentos.length === 0 ? (
                <div style={styles.selectedDocsEmpty}>
                  Selecione os documentos exigidos por este cliente,
                  independentemente de serem enviados por portal ou por e-mail.
                </div>
              ) : (
                <div style={styles.selectedDocsTableWrap}>
                  <table style={styles.selectedDocsTable}>
                    <thead>
                      <tr>
                        <th style={styles.selectedDocsTh}>Documento</th>
                        <th style={styles.selectedDocsTh}>Categoria</th>
                        <th style={styles.selectedDocsTh}>Órgão emissor</th>
                        <th style={styles.selectedDocsTh}>Status</th>
                        <th style={styles.selectedDocsThAction}>Remover</th>
                      </tr>
                    </thead>

                    <tbody>
                      {selectedDocumentos.map((doc) => {
                        const visual = getDocumentoStatusVisual(
                          doc.statusValidade
                        );

                        return (
                          <tr key={doc.id} style={styles.selectedDocsTr}>
                            <td style={styles.selectedDocsTd}>
                              <div style={styles.selectedDocName}>
                                {doc.nome}
                              </div>
                              <div style={styles.selectedDocMeta}>
                                Responsável: {doc.responsavelNome}
                              </div>
                            </td>

                            <td style={styles.selectedDocsTd}>
                              {doc.categoria}
                            </td>
                            <td style={styles.selectedDocsTd}>
                              {doc.orgaoEmissor}
                            </td>

                            <td style={styles.selectedDocsTd}>
                              <span
                                style={{
                                  ...styles.docStatusPill,
                                  background: visual.background,
                                  color: visual.color,
                                  borderColor: visual.border,
                                }}
                              >
                                {getDocumentoStatusLabel(doc.statusValidade)}
                              </span>
                            </td>

                            <td style={styles.selectedDocsTdAction}>
                              <button
                                type="button"
                                style={styles.removeDocButton}
                                onClick={() => onRemoveDocumento(doc.id)}
                                disabled={saving}
                                title="Remover documento exigido"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {selectedDocumentos.length > 0 && hasDocumentoInvalido && (
              <div style={styles.documentRuleWarning}>
                <AlertTriangle size={15} />
                <span>
                  Existem documentos vencidos, pendentes, em validação ou não
                  avaliados. O canal será registrado como{" "}
                  <strong>
                    {getStatusCadastralLabel(statusCadastralCalculado)}
                  </strong>
                  .
                </span>
              </div>
            )}
          </div>

          <div style={styles.formSectionTitle}>
            <UserRound size={14} />
            <span>Responsabilidade interna</span>
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Área responsável</label>
            <input
              style={styles.formInput}
              value={form.areaResponsavel}
              onChange={(event) =>
                onChange("areaResponsavel", event.target.value)
              }
              placeholder="Ex.: Governança"
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Responsável</label>
            <input
              style={styles.formInput}
              value={form.responsavelNome}
              onChange={(event) =>
                onChange("responsavelNome", event.target.value)
              }
              placeholder="Ex.: Guilherme Brito"
              disabled={saving}
            />
          </div>

          <div style={styles.formFieldWide}>
            <label style={styles.formLabel}>Observação</label>
            <textarea
              style={styles.formTextarea}
              value={form.observacao}
              onChange={(event) => onChange("observacao", event.target.value)}
              placeholder="Observações sobre cadastro, acesso, destinatários, pendências ou regra documental do cliente..."
              disabled={saving}
            />
          </div>
        </div>

        <div style={styles.overlayFooter}>
          <button
            type="button"
            style={styles.cancelButton}
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            type="button"
            style={{
              ...styles.saveButton,
              background: canSubmit ? departmentColor : "#94a3b8",
              borderColor: canSubmit ? departmentColor : "#94a3b8",
            }}
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            {saving ? <Loader2 size={15} /> : <Save size={15} />}
            Registrar canal
          </button>
        </div>
      </div>
    </div>
  );
};

const CanalDetailOverlay: React.FC<{
  open: boolean;
  item: PortalCliente | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  editMode: boolean;
  editForm: RegisterCanalForm;
  documentosCatalogo: DocumentoCatalogo[];
  documentosLoading: boolean;
  documentosError: string | null;
  departmentColor: string;
  onClose: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onChange: <K extends keyof RegisterCanalForm>(
    key: K,
    value: RegisterCanalForm[K]
  ) => void;
  onAddDocumento: (documentoId: number) => void;
  onRemoveDocumento: (documentoId: number) => void;
}> = ({
  open,
  item,
  loading,
  saving,
  error,
  editMode,
  editForm,
  documentosCatalogo,
  documentosLoading,
  documentosError,
  departmentColor,
  onClose,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onChange,
  onAddDocumento,
  onRemoveDocumento,
}) => {
  const viewportWidth = useViewportWidth();
  const isMobileViewport = viewportWidth < MOBILE_BREAKPOINT;

  const [documentDropdownOpen, setDocumentDropdownOpen] = useState(false);
  const [documentSearch, setDocumentSearch] = useState("");
  const [docViewMode, setDocViewMode] = useState<
    "todos" | "pendentes" | "vencidos" | "atualizados"
  >("todos");

  useEffect(() => {
    if (!open || !editMode) {
      setDocumentDropdownOpen(false);
      setDocumentSearch("");
      setDocViewMode("todos");
    }
  }, [open, editMode]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !loading && !saving) {
        if (editMode) {
          onCancelEdit();
          return;
        }

        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, loading, saving, editMode, onClose, onCancelEdit]);

  if (!open || !item) return null;

  const documentos = Array.isArray(item.documentos) ? item.documentos : [];
  const statusVisual = getStatusCadastralVisual(item.statusCadastral);

  const selectedIds = new Set(
    editMode
      ? editForm.documentosExigidosIds
      : documentos.map((doc) => Number(doc.id)).filter(Boolean)
  );

  const selectedDocumentosCatalogo = documentosCatalogo.filter((doc) =>
    editForm.documentosExigidosIds.includes(doc.id)
  );

  const selectedStatusCalculado = deriveStatusCadastralFromDocumentos(
    selectedDocumentosCatalogo,
    editForm.statusCadastral
  );

  const selectedStatusVisual = getStatusCadastralVisual(selectedStatusCalculado);

  const availableDocumentos = documentosCatalogo.filter(
    (doc) => !selectedIds.has(doc.id)
  );

  const search = normalizeTextSearch(documentSearch);

  const filteredAvailableDocumentos = availableDocumentos.filter((doc) => {
    if (!search) return true;

    return [
      doc.nome,
      doc.categoria,
      doc.orgaoEmissor,
      doc.responsavelNome,
      doc.statusValidade,
    ].some((field) => normalizeTextSearch(field).includes(search));
  });

  const filteredDocumentos = documentos.filter((doc) => {
    if (docViewMode === "pendentes") {
      return (
        doc.status === "pendente_anexo" ||
        doc.status === "renovacao_pendente" ||
        doc.status === "aguardando_validacao" ||
        doc.status === "nao_avaliado"
      );
    }

    if (docViewMode === "vencidos") {
      return doc.status === "vencido";
    }

    if (docViewMode === "atualizados") {
      return doc.status === "atualizado";
    }

    return true;
  });

  const canalObrigatorioOk =
    editForm.tipoCanal === "portal"
      ? String(editForm.portalNome ?? "").trim().length > 0
      : String(editForm.emailsDestino ?? "").trim().length > 0;

  const canSave =
    String(editForm.clienteNome ?? "").trim().length > 0 &&
    canalObrigatorioOk &&
    !saving;

  const portalTitle =
    item.tipoCanal === "email"
      ? item.emailsDestino || "Envio por e-mail"
      : item.portalNome || "Portal do cliente";

  const portalSubtitle =
    item.tipoCanal === "email"
      ? item.assuntoPadraoEmail || "Canal de envio por e-mail"
      : item.portalUrl || item.usuarioReferencia || "Canal de portal";

  const overlayBackdropStyle: React.CSSProperties = {
    position: "fixed",
    top: isMobileViewport ? 0 : HEADER_HEIGHT,
    left: isMobileViewport ? 0 : LEFTBAR_WIDTH,
    right: 0,
    bottom: 0,
    zIndex: isMobileViewport ? 1400 : 1190,
    background:
      "radial-gradient(circle at top left, rgba(47, 63, 89, 0.10), transparent 34%), rgba(15, 23, 42, 0.30)",
    backdropFilter: "blur(3px)",
    WebkitBackdropFilter: "blur(3px)",
    display: "flex",
    justifyContent: "flex-end",
    boxSizing: "border-box",
  };

  const panelStyle: React.CSSProperties = {
    width: isMobileViewport
      ? "100vw"
      : `min(1140px, calc(100vw - ${LEFTBAR_WIDTH}px))`,
    height: isMobileViewport ? "100vh" : `calc(100vh - ${HEADER_HEIGHT}px)`,
    background: "#ffffff",
    borderLeft: "1px solid #dbe4ef",
    boxShadow: "-18px 0 46px rgba(15, 23, 42, 0.22)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    fontFamily:
      'Nunito, Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: "#0f172a",
  };

  const localStyles: Record<string, React.CSSProperties> = {
    header: {
      padding: "18px 22px 16px",
      borderBottom: "1px solid #e5eaf0",
      borderTop: `3px solid ${departmentColor}`,
      background:
        "linear-gradient(135deg, rgba(248, 250, 252, 0.98), rgba(255, 255, 255, 1))",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 18,
    },

    headerLeft: {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },

    eyebrow: {
      color: "#64748b",
      fontSize: 11,
      fontWeight: 950,
      letterSpacing: 0.45,
      textTransform: "uppercase",
    },

    titleRow: {
      display: "flex",
      alignItems: "center",
      gap: 9,
      flexWrap: "wrap",
    },

    title: {
      margin: 0,
      color: "#0f172a",
      fontSize: 22,
      fontWeight: 950,
      lineHeight: 1.18,
      letterSpacing: "-0.025em",
      overflowWrap: "anywhere",
    },

    subtitle: {
      margin: 0,
      color: "#64748b",
      fontSize: 12.5,
      fontWeight: 760,
      lineHeight: 1.45,
      overflowWrap: "anywhere",
    },

    headerActions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 8,
      flexShrink: 0,
      flexWrap: "wrap",
    },

    body: {
      flex: 1,
      overflowY: "auto",
      background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
      padding: 16,
      display: "flex",
      flexDirection: "column",
      gap: 14,
    },

    actionButtonPrimary: {
      minHeight: 36,
      border: `1px solid ${departmentColor}`,
      background: departmentColor,
      color: "#ffffff",
      padding: "0 13px",
      fontSize: 12,
      fontWeight: 950,
      cursor: "pointer",
      fontFamily: "inherit",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
      whiteSpace: "nowrap",
      opacity: saving ? 0.68 : 1,
    },

    actionButtonGhost: {
      minHeight: 36,
      border: "1px solid #cbd5e1",
      background: "#ffffff",
      color: "#334155",
      padding: "0 13px",
      fontSize: 12,
      fontWeight: 900,
      cursor: "pointer",
      fontFamily: "inherit",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
      whiteSpace: "nowrap",
    },

    closeButton: {
      width: 36,
      height: 36,
      border: "1px solid #cbd5e1",
      background: "#ffffff",
      color: "#334155",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },

    errorBox: {
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#991b1b",
      padding: "10px 12px",
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      fontSize: 12.5,
      fontWeight: 850,
    },

    topGrid: {
      display: "grid",
      gridTemplateColumns: isMobileViewport
        ? "1fr"
        : "minmax(0, 1.55fr) minmax(310px, 0.75fr)",
      gap: 14,
      alignItems: "stretch",
    },

    heroCard: {
      border: "1px solid #e2e8f0",
      background: "#ffffff",
      padding: 15,
      display: "flex",
      flexDirection: "column",
      gap: 14,
      boxShadow: "0 1px 0 rgba(15, 23, 42, 0.03)",
    },

    heroHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
    },

    heroTitleBlock: {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },

    heroTitle: {
      margin: 0,
      color: "#0f172a",
      fontSize: 16,
      fontWeight: 950,
      lineHeight: 1.25,
      overflowWrap: "anywhere",
    },

    heroMeta: {
      color: "#64748b",
      fontSize: 12,
      fontWeight: 760,
      lineHeight: 1.35,
      overflowWrap: "anywhere",
    },

    badge: {
      minHeight: 25,
      padding: "0 9px",
      border: "1px solid #cbd5e1",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
      fontSize: 11.5,
      fontWeight: 950,
      whiteSpace: "nowrap",
    },

    infoGrid: {
      display: "grid",
      gridTemplateColumns: isMobileViewport
        ? "1fr"
        : "repeat(2, minmax(0, 1fr))",
      gap: 8,
    },

    fieldBox: {
      border: "1px solid #edf2f7",
      background: "#f8fafc",
      padding: "9px 10px",
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 5,
    },

    fieldLabel: {
      color: "#64748b",
      fontSize: 11,
      fontWeight: 950,
    },

    fieldValue: {
      color: "#0f172a",
      fontSize: 12.5,
      fontWeight: 900,
      overflowWrap: "anywhere",
      lineHeight: 1.35,
    },

    input: {
      width: "100%",
      height: 36,
      border: "1px solid #cbd5e1",
      background: "#ffffff",
      color: "#0f172a",
      padding: "0 10px",
      fontSize: 12.5,
      fontWeight: 760,
      fontFamily: "inherit",
      outline: "none",
      boxSizing: "border-box",
    },

    textarea: {
      width: "100%",
      minHeight: 82,
      border: "1px solid #cbd5e1",
      background: "#ffffff",
      color: "#0f172a",
      padding: "9px 10px",
      fontSize: 12.5,
      fontWeight: 760,
      fontFamily: "inherit",
      outline: "none",
      resize: "vertical",
      boxSizing: "border-box",
      lineHeight: 1.45,
    },

    sideMetrics: {
      border: "1px solid #e2e8f0",
      background: "#ffffff",
      padding: 13,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      boxShadow: "0 1px 0 rgba(15, 23, 42, 0.03)",
    },

    metricTitle: {
      color: "#0f172a",
      fontSize: 13,
      fontWeight: 950,
      margin: 0,
    },

    metricGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 8,
    },

    metricCard: {
      border: "1px solid #edf2f7",
      background: "#f8fafc",
      padding: 10,
      minHeight: 70,
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },

    metricLabel: {
      color: "#64748b",
      fontSize: 11,
      fontWeight: 900,
    },

    metricValue: {
      color: "#0f172a",
      fontSize: 20,
      fontWeight: 950,
      lineHeight: 1.1,
    },

    section: {
      border: "1px solid #e2e8f0",
      background: "#ffffff",
      padding: 14,
      display: "flex",
      flexDirection: "column",
      gap: 12,
      boxShadow: "0 1px 0 rgba(15, 23, 42, 0.03)",
    },

    sectionHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
    },

    sectionTitleBlock: {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 3,
    },

    sectionTitle: {
      margin: 0,
      color: "#0f172a",
      fontSize: 14,
      fontWeight: 950,
      lineHeight: 1.25,
    },

    sectionHint: {
      color: "#64748b",
      fontSize: 12,
      fontWeight: 760,
      lineHeight: 1.35,
    },

    docToolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      flexWrap: "wrap",
      border: "1px solid #edf2f7",
      background: "#f8fafc",
      padding: 8,
    },

    filterTabs: {
      display: "flex",
      gap: 6,
      flexWrap: "wrap",
    },

    filterTab: {
      height: 30,
      border: "1px solid #cbd5e1",
      background: "#ffffff",
      color: "#475569",
      padding: "0 10px",
      fontSize: 11.5,
      fontWeight: 900,
      cursor: "pointer",
      fontFamily: "inherit",
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
    },

    addDocShell: {
      position: "relative",
      width: isMobileViewport ? "100%" : 360,
    },

    addDocButton: {
      width: "100%",
      minHeight: 34,
      border: `1px solid ${toRgba(departmentColor, 0.32)}`,
      background: "#ffffff",
      color: departmentColor,
      padding: "0 12px",
      fontSize: 12,
      fontWeight: 950,
      cursor: "pointer",
      fontFamily: "inherit",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },

    dropdown: {
      position: "absolute",
      top: "calc(100% + 6px)",
      right: 0,
      width: "min(520px, 92vw)",
      maxHeight: 430,
      border: "1px solid #dbe4ef",
      background: "#ffffff",
      boxShadow: "0 22px 46px rgba(15, 23, 42, 0.18)",
      zIndex: 10,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },

    dropdownSearch: {
      padding: 9,
      borderBottom: "1px solid #e5eaf0",
      display: "flex",
      alignItems: "center",
      gap: 8,
      background: "#f8fafc",
    },

    dropdownSearchInput: {
      flex: 1,
      height: 34,
      border: "1px solid #cbd5e1",
      background: "#ffffff",
      color: "#0f172a",
      padding: "0 10px",
      fontSize: 12.5,
      fontWeight: 760,
      fontFamily: "inherit",
      outline: "none",
    },

    dropdownList: {
      overflowY: "auto",
      padding: 7,
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },

    dropdownItem: {
      width: "100%",
      border: "1px solid #edf2f7",
      background: "#ffffff",
      padding: 10,
      textAlign: "left",
      cursor: "pointer",
      fontFamily: "inherit",
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      gap: 8,
      alignItems: "center",
    },

    dropdownItemTitle: {
      color: "#0f172a",
      fontSize: 12.5,
      fontWeight: 950,
      lineHeight: 1.25,
      overflowWrap: "anywhere",
    },

    dropdownItemMeta: {
      color: "#64748b",
      fontSize: 11.5,
      fontWeight: 760,
      lineHeight: 1.3,
      marginTop: 3,
    },

    dropdownEmpty: {
      border: "1px dashed #cbd5e1",
      background: "#f8fafc",
      color: "#64748b",
      padding: 12,
      fontSize: 12.5,
      fontWeight: 850,
      textAlign: "center",
    },

    dropdownError: {
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#991b1b",
      padding: 10,
      fontSize: 12.5,
      fontWeight: 850,
      display: "flex",
      gap: 7,
      alignItems: "center",
    },

    docGrid: {
      display: "grid",
      gridTemplateColumns: isMobileViewport
        ? "1fr"
        : "repeat(2, minmax(0, 1fr))",
      gap: 10,
    },

    docCard: {
      border: "1px solid #e2e8f0",
      background: "#ffffff",
      padding: 11,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      boxShadow: "0 1px 0 rgba(15, 23, 42, 0.03)",
    },

    docCardTop: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 10,
    },

    docNameBlock: {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },

    docName: {
      color: "#0f172a",
      fontSize: 13,
      fontWeight: 950,
      lineHeight: 1.25,
      overflowWrap: "anywhere",
    },

    docMeta: {
      color: "#64748b",
      fontSize: 11.5,
      fontWeight: 760,
      lineHeight: 1.35,
      overflowWrap: "anywhere",
    },

    docStatusPill: {
      minHeight: 24,
      border: "1px solid #cbd5e1",
      padding: "0 8px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 950,
      whiteSpace: "nowrap",
    },

    docInfoGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 7,
    },

    miniInfo: {
      border: "1px solid #edf2f7",
      background: "#f8fafc",
      padding: 8,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      minWidth: 0,
    },

    miniLabel: {
      color: "#64748b",
      fontSize: 10.5,
      fontWeight: 950,
    },

    miniValue: {
      color: "#0f172a",
      fontSize: 11.5,
      fontWeight: 850,
      overflowWrap: "anywhere",
      lineHeight: 1.25,
    },

    docCardFooter: {
      borderTop: "1px solid #edf2f7",
      paddingTop: 9,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      flexWrap: "wrap",
    },

    removeButton: {
      minHeight: 30,
      border: "1px solid #fecaca",
      background: "#ffffff",
      color: "#991b1b",
      padding: "0 10px",
      fontSize: 11.5,
      fontWeight: 950,
      cursor: "pointer",
      fontFamily: "inherit",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    },

    emptyDocs: {
      border: "1px dashed #cbd5e1",
      background: "#f8fafc",
      color: "#64748b",
      padding: 18,
      minHeight: 120,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      fontSize: 13,
      fontWeight: 850,
      lineHeight: 1.45,
    },

    loadingDocs: {
      border: "1px solid #e2e8f0",
      background: "#ffffff",
      color: "#475569",
      padding: 18,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 9,
      fontSize: 13,
      fontWeight: 850,
    },

    footer: {
      padding: "12px 16px",
      borderTop: "1px solid #e5eaf0",
      background: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
    },

    footerHint: {
      color: "#64748b",
      fontSize: 12,
      fontWeight: 760,
    },

    footerActions: {
      display: "flex",
      gap: 8,
      alignItems: "center",
      justifyContent: "flex-end",
    },
  };

  const renderBadge = (
    label: React.ReactNode,
    visual: { background: string; color: string; border: string; icon?: React.ReactNode }
  ) => (
    <span
      style={{
        ...localStyles.badge,
        background: visual.background,
        color: visual.color,
        borderColor: visual.border,
      }}
    >
      {visual.icon}
      {label}
    </span>
  );

  const renderField = (
    label: string,
    value: React.ReactNode,
    input?: React.ReactNode
  ) => (
    <div style={localStyles.fieldBox}>
      <span style={localStyles.fieldLabel}>{label}</span>
      {editMode && input ? (
        input
      ) : (
        <strong style={localStyles.fieldValue}>
          {value || "Não informado"}
        </strong>
      )}
    </div>
  );

  const renderMiniInfo = (label: string, value: React.ReactNode) => (
    <div style={localStyles.miniInfo}>
      <span style={localStyles.miniLabel}>{label}</span>
      <strong style={localStyles.miniValue}>{value || "—"}</strong>
    </div>
  );

  const renderMetric = (
    label: string,
    value: number,
    color?: string
  ) => (
    <div style={localStyles.metricCard}>
      <span style={localStyles.metricLabel}>{label}</span>
      <strong
        style={{
          ...localStyles.metricValue,
          color: color || "#0f172a",
        }}
      >
        {value}
      </strong>
    </div>
  );

  const renderDocFilterButton = (
    key: "todos" | "pendentes" | "vencidos" | "atualizados",
    label: string,
    count: number
  ) => {
    const active = docViewMode === key;

    return (
      <button
        type="button"
        style={{
          ...localStyles.filterTab,
          ...(active
            ? {
                background: toRgba(departmentColor, 0.08),
                color: departmentColor,
                borderColor: departmentColor,
              }
            : {}),
        }}
        onClick={() => setDocViewMode(key)}
      >
        {label}
        <span>{count}</span>
      </button>
    );
  };

  const pendingDocsCount = documentos.filter(
    (doc) =>
      doc.status === "pendente_anexo" ||
      doc.status === "renovacao_pendente" ||
      doc.status === "aguardando_validacao" ||
      doc.status === "nao_avaliado"
  ).length;

  const vencidosCount = documentos.filter((doc) => doc.status === "vencido").length;
  const atualizadosCount = documentos.filter(
    (doc) => doc.status === "atualizado"
  ).length;

  return (
    <div
      style={overlayBackdropStyle}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !loading && !saving) {
          onClose();
        }
      }}
    >
      <div style={panelStyle} onMouseDown={(event) => event.stopPropagation()}>
        <header style={localStyles.header}>
          <div style={localStyles.headerLeft}>
            <span style={localStyles.eyebrow}>
              {editMode ? "Editando canal do cliente" : "Detalhamento do canal"}
            </span>

            <div style={localStyles.titleRow}>
              <h2 style={localStyles.title}>{portalTitle}</h2>

              {renderBadge(
                getTipoCanalLabel(item.tipoCanal),
                {
                  background: "#ffffff",
                  color: "#334155",
                  border: "#cbd5e1",
                  icon:
                    item.tipoCanal === "email" ? (
                      <Mail size={12} />
                    ) : (
                      <Globe2 size={12} />
                    ),
                }
              )}

              {renderBadge(
                editMode
                  ? getStatusCadastralLabel(selectedStatusCalculado)
                  : getStatusCadastralLabel(item.statusCadastral),
                editMode ? selectedStatusVisual : statusVisual
              )}
            </div>

            <p style={localStyles.subtitle}>
              {item.clienteNome} · {item.clienteCnpj || "CNPJ não informado"} ·{" "}
              {portalSubtitle}
            </p>
          </div>

          <div style={localStyles.headerActions}>
            {!editMode ? (
              <button
                type="button"
                style={localStyles.actionButtonPrimary}
                onClick={onStartEdit}
                disabled={loading}
              >
                <Save size={14} />
                Alterar dados
              </button>
            ) : (
              <>
                <button
                  type="button"
                  style={localStyles.actionButtonGhost}
                  onClick={onCancelEdit}
                  disabled={saving}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  style={{
                    ...localStyles.actionButtonPrimary,
                    background: canSave ? departmentColor : "#94a3b8",
                    borderColor: canSave ? departmentColor : "#94a3b8",
                    cursor: canSave ? "pointer" : "not-allowed",
                  }}
                  onClick={onSaveEdit}
                  disabled={!canSave}
                >
                  {saving ? <Loader2 size={14} /> : <Save size={14} />}
                  Salvar alterações
                </button>
              </>
            )}

            <button
              type="button"
              style={localStyles.closeButton}
              onClick={onClose}
              disabled={saving}
            >
              <X size={17} />
            </button>
          </div>
        </header>

        <main style={localStyles.body}>
          {error && (
            <div style={localStyles.errorBox}>
              <AlertTriangle size={15} />
              <span>{error}</span>
            </div>
          )}

          <section style={localStyles.topGrid}>
            <div style={localStyles.heroCard}>
              <div style={localStyles.heroHeader}>
                <div style={localStyles.heroTitleBlock}>
                  <h3 style={localStyles.heroTitle}>Dados cadastrais do canal</h3>
                  <span style={localStyles.heroMeta}>
                    Informações usadas para rastrear o envio documental, acesso ao
                    portal e responsáveis internos.
                  </span>
                </div>

                {editMode &&
                  renderBadge(
                    "Modo edição",
                    {
                      background: toRgba(departmentColor, 0.08),
                      color: departmentColor,
                      border: toRgba(departmentColor, 0.28),
                    }
                  )}
              </div>

              <div style={localStyles.infoGrid}>
                {renderField(
                  "Cliente",
                  item.clienteNome,
                  <input
                    style={localStyles.input}
                    value={editForm.clienteNome}
                    disabled={saving}
                    onChange={(event) =>
                      onChange("clienteNome", event.target.value)
                    }
                    placeholder="Nome do cliente"
                  />
                )}

                {renderField(
                  "Código interno",
                  item.codigoInterno,
                  <input
                    style={localStyles.input}
                    value={editForm.codigoInterno}
                    disabled={saving}
                    onChange={(event) =>
                      onChange("codigoInterno", event.target.value)
                    }
                    placeholder="Ex.: CLI-MARLIN"
                  />
                )}

                {renderField(
                  "Razão social",
                  item.clienteRazaoSocial,
                  <input
                    style={localStyles.input}
                    value={editForm.clienteRazaoSocial}
                    disabled={saving}
                    onChange={(event) =>
                      onChange("clienteRazaoSocial", event.target.value)
                    }
                    placeholder="Razão social"
                  />
                )}

                {renderField(
                  "CNPJ / Identificação",
                  item.clienteCnpj,
                  <input
                    style={localStyles.input}
                    value={editForm.clienteCnpj}
                    disabled={saving}
                    onChange={(event) =>
                      onChange("clienteCnpj", event.target.value)
                    }
                    placeholder="00.000.000/0000-00"
                  />
                )}

                <div style={localStyles.fieldBox}>
                  <span style={localStyles.fieldLabel}>Tipo de canal</span>

                  {editMode ? (
                    <select
                      style={localStyles.input}
                      value={editForm.tipoCanal}
                      disabled={saving}
                      onChange={(event) =>
                        onChange(
                          "tipoCanal",
                          event.target.value as TipoCanalEnvio
                        )
                      }
                    >
                      <option value="portal">Portal do cliente</option>
                      <option value="email">Envio por e-mail</option>
                    </select>
                  ) : (
                    <strong style={localStyles.fieldValue}>
                      {getTipoCanalLabel(item.tipoCanal)}
                    </strong>
                  )}
                </div>

                <div style={localStyles.fieldBox}>
                  <span style={localStyles.fieldLabel}>Status cadastral</span>

                  {editMode ? (
                    <select
                      style={localStyles.input}
                      value={editForm.statusCadastral}
                      disabled={saving}
                      onChange={(event) =>
                        onChange(
                          "statusCadastral",
                          event.target.value as StatusCadastral
                        )
                      }
                    >
                      <option value="regular">Regular</option>
                      <option value="pendente_documental">
                        Pendente documental
                      </option>
                      <option value="documento_vencido">Documento vencido</option>
                      <option value="aguardando_validacao">
                        Aguardando validação
                      </option>
                      <option value="bloqueado">Bloqueado</option>
                      <option value="nao_cadastrado">Não cadastrado</option>
                      <option value="nao_avaliado">Não avaliado</option>
                    </select>
                  ) : (
                    <strong style={localStyles.fieldValue}>
                      {getStatusCadastralLabel(item.statusCadastral)}
                    </strong>
                  )}
                </div>

                {editMode ? (
                  editForm.tipoCanal === "portal" ? (
                    <>
                      {renderField(
                        "Nome do portal",
                        item.portalNome,
                        <input
                          style={localStyles.input}
                          value={editForm.portalNome}
                          disabled={saving}
                          onChange={(event) =>
                            onChange("portalNome", event.target.value)
                          }
                          placeholder="Nome do portal"
                        />
                      )}

                      {renderField(
                        "Usuário / referência",
                        item.usuarioReferencia,
                        <input
                          style={localStyles.input}
                          value={editForm.usuarioReferencia}
                          disabled={saving}
                          onChange={(event) =>
                            onChange("usuarioReferencia", event.target.value)
                          }
                          placeholder="Usuário, e-mail ou referência"
                        />
                      )}

                      {renderField(
                        "URL do portal",
                        item.portalUrl,
                        <input
                          style={localStyles.input}
                          value={editForm.portalUrl}
                          disabled={saving}
                          onChange={(event) =>
                            onChange("portalUrl", event.target.value)
                          }
                          placeholder="https://..."
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {renderField(
                        "E-mails de destino",
                        item.emailsDestino,
                        <input
                          style={localStyles.input}
                          value={editForm.emailsDestino}
                          disabled={saving}
                          onChange={(event) =>
                            onChange("emailsDestino", event.target.value)
                          }
                          placeholder="email@cliente.com.br; outro@cliente.com.br"
                        />
                      )}

                      {renderField(
                        "Assunto padrão",
                        item.assuntoPadraoEmail,
                        <input
                          style={localStyles.input}
                          value={editForm.assuntoPadraoEmail}
                          disabled={saving}
                          onChange={(event) =>
                            onChange("assuntoPadraoEmail", event.target.value)
                          }
                          placeholder="Assunto padrão do envio"
                        />
                      )}
                    </>
                  )
                ) : item.tipoCanal === "portal" ? (
                  <>
                    {renderField("Portal", item.portalNome)}
                    {renderField("URL", item.portalUrl)}
                    {renderField("Usuário / referência", item.usuarioReferencia)}
                  </>
                ) : (
                  <>
                    {renderField("E-mails de destino", item.emailsDestino)}
                    {renderField("Assunto padrão", item.assuntoPadraoEmail)}
                    {renderField("Último envio", item.ultimoEnvioEmail)}
                  </>
                )}

                {renderField(
                  "Área responsável",
                  item.areaResponsavel,
                  <input
                    style={localStyles.input}
                    value={editForm.areaResponsavel}
                    disabled={saving}
                    onChange={(event) =>
                      onChange("areaResponsavel", event.target.value)
                    }
                    placeholder="Ex.: Governança"
                  />
                )}

                {renderField(
                  "Responsável",
                  item.responsavelNome,
                  <input
                    style={localStyles.input}
                    value={editForm.responsavelNome}
                    disabled={saving}
                    onChange={(event) =>
                      onChange("responsavelNome", event.target.value)
                    }
                    placeholder="Responsável interno"
                  />
                )}
              </div>

              {editMode ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {editForm.tipoCanal === "email" && (
                    <div style={localStyles.fieldBox}>
                      <span style={localStyles.fieldLabel}>
                        Procedimento de envio por e-mail
                      </span>

                      <textarea
                        style={localStyles.textarea}
                        value={editForm.procedimentoEnvio}
                        disabled={saving}
                        onChange={(event) =>
                          onChange("procedimentoEnvio", event.target.value)
                        }
                        placeholder="Descreva o procedimento de envio, destinatários, cópias, frequência e regra operacional."
                      />
                    </div>
                  )}

                  <div style={localStyles.fieldBox}>
                    <span style={localStyles.fieldLabel}>Observação</span>

                    <textarea
                      style={localStyles.textarea}
                      value={editForm.observacao}
                      disabled={saving}
                      onChange={(event) =>
                        onChange("observacao", event.target.value)
                      }
                      placeholder="Observações sobre cadastro, acesso, pendências ou regra documental."
                    />
                  </div>
                </div>
              ) : (
                <>
                  {item.tipoCanal === "email" && item.procedimentoEnvio && (
                    <div style={localStyles.fieldBox}>
                      <span style={localStyles.fieldLabel}>
                        Procedimento de envio
                      </span>
                      <strong style={localStyles.fieldValue}>
                        {item.procedimentoEnvio}
                      </strong>
                    </div>
                  )}

                  {item.observacao && (
                    <div style={localStyles.fieldBox}>
                      <span style={localStyles.fieldLabel}>Observação</span>
                      <strong style={localStyles.fieldValue}>
                        {item.observacao}
                      </strong>
                    </div>
                  )}
                </>
              )}
            </div>

            <aside style={localStyles.sideMetrics}>
              <h3 style={localStyles.metricTitle}>Resumo documental</h3>

              <div style={localStyles.metricGrid}>
                {renderMetric("Exigidos", item.documentosExigidos)}
                {renderMetric("Atualizados", item.documentosAtualizados, "#166534")}
                {renderMetric("Pendentes", item.documentosPendentes, "#9a3412")}
                {renderMetric("Vencidos", item.documentosVencidos, "#991b1b")}
                {renderMetric(
                  "Validação",
                  item.documentosAguardandoValidacao,
                  "#6d28d9"
                )}
                {renderMetric(
                  "Renovação",
                  item.documentosRenovacaoPendente,
                  "#1d4ed8"
                )}
              </div>

              <div
                style={{
                  border: `1px solid ${toRgba(departmentColor, 0.22)}`,
                  background: toRgba(departmentColor, 0.045),
                  padding: 10,
                  color: "#475569",
                  fontSize: 12,
                  fontWeight: 760,
                  lineHeight: 1.45,
                }}
              >
                {editMode
                  ? "No modo edição, adicione ou remova documentos exigidos pelo cliente. A atualização é aplicada diretamente ao vínculo do portal."
                  : "Os documentos abaixo representam a exigência operacional atual deste canal de envio."}
              </div>
            </aside>
          </section>

          <section style={localStyles.section}>
            <div style={localStyles.sectionHeader}>
              <div style={localStyles.sectionTitleBlock}>
                <h3 style={localStyles.sectionTitle}>Documentos vinculados</h3>
                <span style={localStyles.sectionHint}>
                  {loading
                    ? "Carregando documentos vinculados..."
                    : `${documentos.length} ${plural(
                        documentos.length,
                        "documento vinculado",
                        "documentos vinculados"
                      )} neste canal.`}
                </span>
              </div>

              {editMode && (
                <div style={localStyles.addDocShell}>
                  <button
                    type="button"
                    style={localStyles.addDocButton}
                    disabled={saving || documentosLoading}
                    onClick={() => setDocumentDropdownOpen((prev) => !prev)}
                  >
                    <span>
                      {documentosLoading
                        ? "Carregando catálogo..."
                        : "Adicionar documento"}
                    </span>

                    {documentosLoading ? (
                      <Loader2 size={15} />
                    ) : (
                      <ChevronDown size={15} />
                    )}
                  </button>

                  {documentDropdownOpen && (
                    <div style={localStyles.dropdown}>
                      <div style={localStyles.dropdownSearch}>
                        <Search size={14} color="#64748b" />

                        <input
                          style={localStyles.dropdownSearchInput}
                          value={documentSearch}
                          placeholder="Buscar por documento, categoria ou órgão..."
                          onChange={(event) =>
                            setDocumentSearch(event.target.value)
                          }
                          autoFocus
                        />
                      </div>

                      <div style={localStyles.dropdownList}>
                        {documentosError && (
                          <div style={localStyles.dropdownError}>
                            <AlertTriangle size={14} />
                            <span>{documentosError}</span>
                          </div>
                        )}

                        {!documentosError &&
                          filteredAvailableDocumentos.length === 0 && (
                            <div style={localStyles.dropdownEmpty}>
                              Nenhum documento disponível para adicionar.
                            </div>
                          )}

                        {!documentosError &&
                          filteredAvailableDocumentos.map((doc) => {
                            const visual = getDocumentoStatusVisual(
                              doc.statusValidade
                            );

                            return (
                              <button
                                key={doc.id}
                                type="button"
                                style={localStyles.dropdownItem}
                                disabled={saving}
                                onClick={() => {
                                  onAddDocumento(doc.id);
                                  setDocumentDropdownOpen(false);
                                  setDocumentSearch("");
                                }}
                              >
                                <div style={{ minWidth: 0 }}>
                                  <div style={localStyles.dropdownItemTitle}>
                                    {doc.nome}
                                  </div>
                                  <div style={localStyles.dropdownItemMeta}>
                                    {doc.categoria} · {doc.orgaoEmissor}
                                  </div>
                                  <div style={localStyles.dropdownItemMeta}>
                                    Responsável: {doc.responsavelNome}
                                  </div>
                                </div>

                                <span
                                  style={{
                                    ...localStyles.docStatusPill,
                                    background: visual.background,
                                    color: visual.color,
                                    borderColor: visual.border,
                                  }}
                                >
                                  {getDocumentoStatusLabel(doc.statusValidade)}
                                </span>
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={localStyles.docToolbar}>
              <div style={localStyles.filterTabs}>
                {renderDocFilterButton("todos", "Todos", documentos.length)}
                {renderDocFilterButton("pendentes", "Pendências", pendingDocsCount)}
                {renderDocFilterButton("vencidos", "Vencidos", vencidosCount)}
                {renderDocFilterButton("atualizados", "Atualizados", atualizadosCount)}
              </div>

              <span style={localStyles.sectionHint}>
                {filteredDocumentos.length} exibido(s)
              </span>
            </div>

            {loading && (
              <div style={localStyles.loadingDocs}>
                <Loader2 size={17} color={departmentColor} />
                Carregando documentos vinculados...
              </div>
            )}

            {!loading && filteredDocumentos.length === 0 && (
              <div style={localStyles.emptyDocs}>
                {docViewMode === "todos"
                  ? "Nenhum documento vinculado foi retornado pela API para este canal."
                  : "Nenhum documento encontrado neste filtro."}
              </div>
            )}

            {!loading && filteredDocumentos.length > 0 && (
              <div style={localStyles.docGrid}>
                {filteredDocumentos.map((doc) => {
                  const visual = getDocumentoStatusVisual(doc.status);

                  return (
                    <article
                      key={`${doc.id}-${doc.documentoPortalId ?? doc.documentoNome}`}
                      style={{
                        ...localStyles.docCard,
                        boxShadow:
                          doc.status === "vencido"
                            ? "inset 4px 0 0 #991b1b, 0 1px 0 rgba(15, 23, 42, 0.03)"
                            : doc.status === "atualizado"
                            ? "inset 4px 0 0 #166534, 0 1px 0 rgba(15, 23, 42, 0.03)"
                            : "inset 4px 0 0 #cbd5e1, 0 1px 0 rgba(15, 23, 42, 0.03)",
                      }}
                    >
                      <div style={localStyles.docCardTop}>
                        <div style={localStyles.docNameBlock}>
                          <strong style={localStyles.docName}>
                            {doc.documentoNome}
                          </strong>

                          <span style={localStyles.docMeta}>
                            {doc.categoria} · {doc.orgaoEmissor}
                          </span>
                        </div>

                        <span
                          style={{
                            ...localStyles.docStatusPill,
                            background: visual.background,
                            color: visual.color,
                            borderColor: visual.border,
                          }}
                        >
                          {getDocumentoStatusLabel(doc.status)}
                        </span>
                      </div>

                      <div style={localStyles.docInfoGrid}>
                        {renderMiniInfo("Validade", doc.validade || "Não informado")}
                        {renderMiniInfo(
                          "Arquivo",
                          doc.arquivoNome || "Sem arquivo"
                        )}
                        {renderMiniInfo("Responsável", doc.responsavel)}
                      </div>

                      {doc.observacao && (
                        <div style={localStyles.fieldBox}>
                          <span style={localStyles.fieldLabel}>Observação</span>
                          <strong style={localStyles.fieldValue}>
                            {doc.observacao}
                          </strong>
                        </div>
                      )}

                      <div style={localStyles.docCardFooter}>
                        <span style={localStyles.docMeta}>
                          Atualizado em: {doc.atualizadoEm || "Não informado"}
                        </span>

                        {editMode && (
                          <button
                            type="button"
                            style={localStyles.removeButton}
                            disabled={saving}
                            onClick={() => onRemoveDocumento(doc.id)}
                            title="Remover exigência documental deste portal"
                          >
                            <Trash2 size={13} />
                            Remover vínculo
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </main>

        {editMode && (
          <footer style={localStyles.footer}>
            <span style={localStyles.footerHint}>
              As alterações de dados e vínculos documentais serão refletidas no
              canal do cliente após salvar.
            </span>

            <div style={localStyles.footerActions}>
              <button
                type="button"
                style={localStyles.actionButtonGhost}
                onClick={onCancelEdit}
                disabled={saving}
              >
                Cancelar
              </button>

              <button
                type="button"
                style={{
                  ...localStyles.actionButtonPrimary,
                  background: canSave ? departmentColor : "#94a3b8",
                  borderColor: canSave ? departmentColor : "#94a3b8",
                  cursor: canSave ? "pointer" : "not-allowed",
                }}
                onClick={onSaveEdit}
                disabled={!canSave}
              >
                {saving ? <Loader2 size={14} /> : <Save size={14} />}
                Salvar alterações
              </button>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

const GovernancaPortaisClientesLayout: React.FC<
  GovernancaPortaisClientesLayoutProps
> = ({ section }) => {
  const navigate = useNavigate();
  const { departmentColor } = useTheme();
  const auth = useAuth() as any;

  const sessao = auth?.sessao;
  const token = getAuthToken(auth);

  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;
  const activeSection = section || getSectionFromPath(window.location.pathname);

  const empresaId = useMemo(() => getEmpresaIdFromSessao(sessao), [sessao]);
  const moduloId = useMemo(() => getModuloIdFromSessao(sessao), [sessao]);
  const moduloDocumentosId = useMemo(
    () => getModuloDocumentosIdFromSessao(sessao),
    [sessao]
  );

  const [filters, setFilters] = useState<CanalFilterState>(initialFilters);
  const [itens, setItens] = useState<PortalCliente[]>([]);
  const [documentosCatalogo, setDocumentosCatalogo] = useState<
    DocumentoCatalogo[]
  >([]);
  const [documentosLoading, setDocumentosLoading] = useState(false);
  const [documentosError, setDocumentosError] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerSaving, setRegisterSaving] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerForm, setRegisterForm] = useState<RegisterCanalForm>(
    initialRegisterCanalForm
  );

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<PortalCliente | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailEditMode, setDetailEditMode] = useState(false);
  const [detailForm, setDetailForm] = useState<RegisterCanalForm>(
    initialRegisterCanalForm
  );

  const requestHeaders = useMemo(
    () => ({
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const loadCanais = async () => {
    setLoading(true);
    setPageError(null);

    try {
      if (!empresaId || !moduloId) {
        throw new Error(
          "Empresa ou módulo de governança não identificado na sessão."
        );
      }

      const params = new URLSearchParams({
        empresa_id: String(empresaId),
        modulo_id: String(moduloId),
      });

      const response = await fetch(`${API_PORTAIS_CLIENTES_URL}?${params}`, {
        method: "GET",
        headers: requestHeaders,
        credentials: "include",
      });

      const data = (await response
        .json()
        .catch(() => null)) as ApiPortaisClientesResponse | null;

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.erro ||
            `Falha ao carregar canais de clientes. Status HTTP ${response.status}.`
        );
      }

      const items = Array.isArray(data.items) ? data.items : [];
      setItens(items.map(normalizePortalFromApi));
    } catch (error: any) {
      setPageError(error?.message || "Falha ao carregar canais de clientes.");
      setItens([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentosCatalogo = async () => {
    setDocumentosLoading(true);
    setDocumentosError(null);

    try {
      if (!empresaId) {
        throw new Error("Empresa não identificada na sessão.");
      }

      if (!moduloDocumentosId) {
        throw new Error(
          "Módulo de Certidões e Documentos não identificado na sessão."
        );
      }

      const params = new URLSearchParams({
        empresa_id: String(empresaId),
        modulo_id: String(moduloDocumentosId),
      });

      const response = await fetch(`${API_DOCUMENTOS_URL}?${params}`, {
        method: "GET",
        headers: requestHeaders,
        credentials: "include",
      });

      const data = (await response
        .json()
        .catch(() => null)) as ApiDocumentosResponse | null;

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.erro ||
            `Falha ao carregar documentos. Status HTTP ${response.status}.`
        );
      }

      const normalized = getDocumentosArrayFromApi(data)
        .map(normalizeDocumentoFromApi)
        .filter(Boolean) as DocumentoCatalogo[];

      normalized.sort((a, b) => {
        const cat = a.categoria.localeCompare(b.categoria, "pt-BR");
        if (cat !== 0) return cat;
        return a.nome.localeCompare(b.nome, "pt-BR");
      });

      setDocumentosCatalogo(normalized);
    } catch (error: any) {
      setDocumentosError(error?.message || "Falha ao carregar documentos.");
      setDocumentosCatalogo([]);
    } finally {
      setDocumentosLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;

    async function run() {
      setLoading(true);
      setPageError(null);

      try {
        if (!empresaId || !moduloId) {
          throw new Error(
            "Empresa ou módulo de governança não identificado na sessão."
          );
        }

        const params = new URLSearchParams({
          empresa_id: String(empresaId),
          modulo_id: String(moduloId),
        });

        const response = await fetch(`${API_PORTAIS_CLIENTES_URL}?${params}`, {
          method: "GET",
          headers: requestHeaders,
          credentials: "include",
        });

        const data = (await response
          .json()
          .catch(() => null)) as ApiPortaisClientesResponse | null;

        if (!response.ok || !data?.ok) {
          throw new Error(
            data?.erro ||
              `Falha ao carregar canais de clientes. Status HTTP ${response.status}.`
          );
        }

        if (!alive) return;

        const items = Array.isArray(data.items) ? data.items : [];
        setItens(items.map(normalizePortalFromApi));
      } catch (error: any) {
        if (!alive) return;

        setPageError(error?.message || "Falha ao carregar canais de clientes.");
        setItens([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();

    return () => {
      alive = false;
    };
  }, [empresaId, moduloId, requestHeaders, reloadKey]);

  useEffect(() => {
    if (!registerOpen) return;
    loadDocumentosCatalogo();
  }, [registerOpen, empresaId, moduloDocumentosId, requestHeaders]);

  const updateRegisterForm = <K extends keyof RegisterCanalForm>(
    key: K,
    value: RegisterCanalForm[K]
  ) => {
    setRegisterForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "tipoCanal" && value === "email"
        ? { portalNome: "", portalUrl: "", usuarioReferencia: "" }
        : {}),
      ...(key === "tipoCanal" && value === "portal"
        ? { emailsDestino: "", assuntoPadraoEmail: "", procedimentoEnvio: "" }
        : {}),
    }));
  };

  const updateFilter = <K extends keyof CanalFilterState>(
    key: K,
    value: CanalFilterState[K]
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleAddDocumentoExigido = (documentoId: number) => {
    setRegisterForm((prev) => {
      if (prev.documentosExigidosIds.includes(documentoId)) return prev;

      return {
        ...prev,
        documentosExigidosIds: [...prev.documentosExigidosIds, documentoId],
      };
    });
  };

  const handleRemoveDocumentoExigido = (documentoId: number) => {
    setRegisterForm((prev) => ({
      ...prev,
      documentosExigidosIds: prev.documentosExigidosIds.filter(
        (id) => id !== documentoId
      ),
    }));
  };

  const closeDetailOverlay = () => {
    if (detailSaving) return;

    setDetailOpen(false);
    setDetailItem(null);
    setDetailError(null);
    setDetailLoading(false);
    setDetailSaving(false);
    setDetailEditMode(false);
    setDetailForm(initialRegisterCanalForm);
  };

  const openDetailOverlay = async (item: PortalCliente) => {
    setDetailOpen(true);
    setDetailItem(item);
    setDetailForm(portalToRegisterCanalForm(item));
    setDetailEditMode(false);
    setDetailError(null);

    if (!empresaId || !moduloId) {
      setDetailError(
        "Empresa ou módulo de governança não identificado na sessão."
      );
      return;
    }

    setDetailLoading(true);

    try {
      const params = new URLSearchParams({
        empresa_id: String(empresaId),
        modulo_id: String(moduloId),
        portal_id: String(item.id),
        include_documentos: "true",
      });

      const response = await fetch(`${API_PORTAIS_CLIENTES_URL}?${params}`, {
        method: "GET",
        headers: requestHeaders,
        credentials: "include",
      });

      const data = (await response
        .json()
        .catch(() => null)) as ApiPortalClienteDetalheResponse | null;

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.erro ||
            `Falha ao carregar documentos do canal. Status HTTP ${response.status}.`
        );
      }

      const detalheRaw =
        data.portal ||
        data.item ||
        (Array.isArray(data.items)
          ? data.items.find((row) => Number(row.id) === Number(item.id))
          : null);

      const detalhado = detalheRaw ? normalizePortalFromApi(detalheRaw) : item;

      setDetailItem(detalhado);
      setDetailForm(portalToRegisterCanalForm(detalhado));
      setItens((prev) =>
        prev.map((row) =>
          Number(row.id) === Number(detalhado.id) ? detalhado : row
        )
      );
    } catch (error: any) {
      setDetailError(
        error?.message ||
          "Não foi possível carregar os documentos vinculados a este canal."
      );
    } finally {
      setDetailLoading(false);
    }
  };


  const startDetailEdit = () => {
    if (!detailItem) return;

    setDetailError(null);
    setDetailForm(portalToRegisterCanalForm(detailItem));
    setDetailEditMode(true);

    if (documentosCatalogo.length === 0 && !documentosLoading) {
      loadDocumentosCatalogo();
    }
  };

  const cancelDetailEdit = () => {
    if (detailSaving) return;

    setDetailEditMode(false);
    setDetailError(null);

    if (detailItem) {
      setDetailForm(portalToRegisterCanalForm(detailItem));
    }
  };

  const updateDetailForm = <K extends keyof RegisterCanalForm>(
    key: K,
    value: RegisterCanalForm[K]
  ) => {
    setDetailForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "tipoCanal" && value === "email"
        ? { portalNome: "", portalUrl: "", usuarioReferencia: "" }
        : {}),
      ...(key === "tipoCanal" && value === "portal"
        ? { emailsDestino: "", assuntoPadraoEmail: "", procedimentoEnvio: "" }
        : {}),
    }));
  };

  const refreshDetailItem = async (baseItem?: PortalCliente | null) => {
    const item = baseItem || detailItem;
    if (!item || !empresaId || !moduloId) return;

    const params = new URLSearchParams({
      empresa_id: String(empresaId),
      modulo_id: String(moduloId),
      portal_id: String(item.id),
      include_documentos: "true",
    });

    const response = await fetch(`${API_PORTAIS_CLIENTES_URL}?${params}`, {
      method: "GET",
      headers: requestHeaders,
      credentials: "include",
    });

    const data = (await response
      .json()
      .catch(() => null)) as ApiPortalClienteDetalheResponse | null;

    if (!response.ok || !data?.ok) {
      throw new Error(
        data?.erro ||
          `Falha ao recarregar canal. Status HTTP ${response.status}.`
      );
    }

    const detalheRaw = data.portal || data.item;
    const detalhado = detalheRaw ? normalizePortalFromApi(detalheRaw) : item;

    setDetailItem(detalhado);
    setDetailForm(portalToRegisterCanalForm(detalhado));
    upsertItemNaLista(detalhado);
  };

  const handleAddDocumentoDetail = async (documentoId: number) => {
    if (detailSaving) return;

    setDetailError(null);

    try {
      if (!empresaId || !moduloId || !detailItem) {
        throw new Error("Empresa, módulo ou portal não identificado.");
      }

      setDetailSaving(true);

      const response = await fetch(API_DOCUMENTOS_PORTAIS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...requestHeaders,
        },
        credentials: "include",
        body: JSON.stringify({
          empresa_id: empresaId,
          modulo_id: moduloId,
          documento_id: documentoId,
          empresa_cliente_portal_id: detailItem.id,
          exige_documento: true,
          status_operacional: "pendente_anexo",
          observacao: "Documento vinculado pelo painel de portais.",
        }),
      });

      const data = (await response.json().catch(() => null)) as {
        ok?: boolean;
        erro?: string;
      } | null;

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.erro ||
            `Falha ao vincular documento ao portal. Status HTTP ${response.status}.`
        );
      }

      await refreshDetailItem(detailItem);
      setReloadKey((prev) => prev + 1);
    } catch (error: any) {
      setDetailError(error?.message || "Falha ao vincular documento ao portal.");
    } finally {
      setDetailSaving(false);
    }
  };

  const handleRemoveDocumentoDetail = async (documentoId: number) => {
    if (detailSaving) return;

    setDetailError(null);

    try {
      if (!empresaId || !moduloId || !detailItem) {
        throw new Error("Empresa, módulo ou portal não identificado.");
      }

      setDetailSaving(true);

      const response = await fetch(API_DOCUMENTOS_PORTAIS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...requestHeaders,
        },
        credentials: "include",
        body: JSON.stringify({
          empresa_id: empresaId,
          modulo_id: moduloId,
          documento_id: documentoId,
          empresa_cliente_portal_id: detailItem.id,
          exige_documento: false,
          status_operacional: "nao_exigido",
          observacao: "Documento removido da lista de exigências do portal.",
        }),
      });

      const data = (await response.json().catch(() => null)) as {
        ok?: boolean;
        erro?: string;
      } | null;

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.erro ||
            `Falha ao remover vínculo do documento. Status HTTP ${response.status}.`
        );
      }

      await refreshDetailItem(detailItem);
      setReloadKey((prev) => prev + 1);
    } catch (error: any) {
      setDetailError(error?.message || "Falha ao remover vínculo do documento.");
    } finally {
      setDetailSaving(false);
    }
  };

  const handleSaveDetailCanal = async () => {
    if (detailSaving || !detailItem) return;

    setDetailError(null);

    const clienteNome = String(detailForm.clienteNome ?? "").trim();
    const portalNome = String(detailForm.portalNome ?? "").trim();
    const emailsDestino = String(detailForm.emailsDestino ?? "").trim();

    if (!clienteNome) {
      setDetailError("Informe o nome do cliente.");
      return;
    }

    if (detailForm.tipoCanal === "portal" && !portalNome) {
      setDetailError("Informe o nome do portal.");
      return;
    }

    if (detailForm.tipoCanal === "email" && !emailsDestino) {
      setDetailError("Informe ao menos um e-mail de destino.");
      return;
    }

    if (!empresaId || !moduloId) {
      setDetailError("Empresa ou módulo de governança não identificado na sessão.");
      return;
    }

    setDetailSaving(true);

    try {
      const selectedDocumentos = documentosCatalogo.filter((doc) =>
        detailForm.documentosExigidosIds.includes(doc.id)
      );

      const statusCadastralCalculado = deriveStatusCadastralFromDocumentos(
        selectedDocumentos,
        detailForm.statusCadastral
      );

      const payload = {
        id: detailItem.id,
        portalId: detailItem.id,
        portal_id: detailItem.id,

        empresa_id: empresaId,
        modulo_id: moduloId,

        tipoCanal: detailForm.tipoCanal,
        tipo_canal: detailForm.tipoCanal,

        clienteNome,
        clienteRazaoSocial: detailForm.clienteRazaoSocial,
        clienteCnpj: detailForm.clienteCnpj,
        codigoInterno: detailForm.codigoInterno,

        portalNome:
          detailForm.tipoCanal === "portal"
            ? portalNome
            : detailForm.portalNome || "Envio por e-mail",
        portalUrl: detailForm.tipoCanal === "portal" ? detailForm.portalUrl : "",
        usuarioReferencia:
          detailForm.tipoCanal === "portal" ? detailForm.usuarioReferencia : "",

        emailsDestino: detailForm.tipoCanal === "email" ? detailForm.emailsDestino : "",
        assuntoPadraoEmail:
          detailForm.tipoCanal === "email" ? detailForm.assuntoPadraoEmail : "",
        procedimentoEnvio:
          detailForm.tipoCanal === "email" ? detailForm.procedimentoEnvio : "",

        areaResponsavel: detailForm.areaResponsavel,
        responsavelNome: detailForm.responsavelNome,
        statusCadastral: statusCadastralCalculado,
        observacao: detailForm.observacao,

        documentosExigidosIds: detailForm.documentosExigidosIds,
        documentoModuloId: moduloDocumentosId,
      };

      const response = await fetch(API_PORTAIS_CLIENTES_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...requestHeaders,
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = (await response
        .json()
        .catch(() => null)) as ApiPostPortalClienteResponse | null;

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.erro ||
            `Falha ao atualizar canal do cliente. Status HTTP ${response.status}.`
        );
      }

      await refreshDetailItem(detailItem);
      setDetailEditMode(false);
      setReloadKey((prev) => prev + 1);
    } catch (error: any) {
      setDetailError(error?.message || "Falha ao atualizar canal do cliente.");
    } finally {
      setDetailSaving(false);
    }
  };

  const filteredBySection = useMemo(() => {
    switch (activeSection) {
      case "pendencias":
        return itens.filter(
          (item) =>
            item.documentosPendentes > 0 ||
            item.statusCadastral === "pendente_documental"
        );

      case "aguardando-validacao":
        return itens.filter(
          (item) =>
            item.documentosAguardandoValidacao > 0 ||
            item.statusCadastral === "aguardando_validacao"
        );

      case "vencidos":
        return itens.filter(
          (item) =>
            item.documentosVencidos > 0 ||
            item.statusCadastral === "documento_vencido"
        );

      case "atualizados":
        return itens.filter((item) => item.statusCadastral === "regular");

      case "clientes-portais":
      case "visao-geral":
      default:
        return itens;
    }
  }, [activeSection, itens]);

  const filteredItems = useMemo(() => {
    const clienteSearch = normalizeTextSearch(filters.cliente);
    const canalSearch = normalizeTextSearch(filters.canal);

    return filteredBySection.filter((item) => {
      const clienteMatch =
        !clienteSearch ||
        [
          item.clienteNome,
          item.clienteRazaoSocial,
          item.clienteCnpj,
          item.codigoInterno,
        ].some((field) => normalizeTextSearch(field).includes(clienteSearch));

      const canalMatch =
        !canalSearch ||
        [
          item.portalNome,
          item.portalUrl,
          item.usuarioReferencia,
          item.emailsDestino,
          item.assuntoPadraoEmail,
          item.procedimentoEnvio,
        ].some((field) => normalizeTextSearch(field).includes(canalSearch));

      const tipoMatch =
        !filters.tipoCanal || item.tipoCanal === filters.tipoCanal;

      const statusMatch =
        !filters.statusCadastral ||
        item.statusCadastral === filters.statusCadastral;

      const responsavelMatch =
        !filters.responsavel ||
        normalizeTextSearch(item.areaResponsavel).includes(
          normalizeTextSearch(filters.responsavel)
        ) ||
        normalizeTextSearch(item.responsavelNome).includes(
          normalizeTextSearch(filters.responsavel)
        );

      const pendenciaMatch =
        !filters.pendencia ||
        (filters.pendencia === "com_pendencia" && hasPendencia(item)) ||
        (filters.pendencia === "sem_pendencia" && !hasPendencia(item)) ||
        (filters.pendencia === "vencidos" && item.documentosVencidos > 0) ||
        (filters.pendencia === "pendente_anexo" &&
          item.documentosPendentes > 0) ||
        (filters.pendencia === "aguardando_validacao" &&
          item.documentosAguardandoValidacao > 0);

      return (
        clienteMatch &&
        canalMatch &&
        tipoMatch &&
        statusMatch &&
        responsavelMatch &&
        pendenciaMatch
      );
    });
  }, [filteredBySection, filters]);

  const resumo = useMemo(() => {
    return {
      total: itens.length,
      portal: itens.filter((item) => item.tipoCanal === "portal").length,
      email: itens.filter((item) => item.tipoCanal === "email").length,
      pendentes: itens.filter(hasPendencia).length,
      vencidos: itens.reduce((acc, item) => acc + item.documentosVencidos, 0),
      atualizados: itens.filter((item) => item.statusCadastral === "regular")
        .length,
    };
  }, [itens]);

  const openRegisterOverlay = () => {
    setRegisterError(null);
    setRegisterForm(initialRegisterCanalForm);
    setRegisterOpen(true);
  };

  const closeRegisterOverlay = () => {
    if (registerSaving) return;
    setRegisterOpen(false);
    setRegisterError(null);
  };

  const upsertItemNaLista = (item: PortalCliente) => {
    setItens((prev) => {
      const exists = prev.some((row) => Number(row.id) === Number(item.id));

      if (exists) {
        return prev.map((row) =>
          Number(row.id) === Number(item.id) ? item : row
        );
      }

      return [item, ...prev];
    });
  };

  const handleRegisterCanal = async () => {
    setRegisterError(null);

    const clienteNome = String(registerForm.clienteNome ?? "").trim();
    const portalNome = String(registerForm.portalNome ?? "").trim();
    const emailsDestino = String(registerForm.emailsDestino ?? "").trim();

    if (!clienteNome) {
      setRegisterError("Informe o nome do cliente.");
      return;
    }

    if (registerForm.tipoCanal === "portal" && !portalNome) {
      setRegisterError("Informe o nome do portal.");
      return;
    }

    if (registerForm.tipoCanal === "email" && !emailsDestino) {
      setRegisterError("Informe ao menos um e-mail de destino.");
      return;
    }

    if (!empresaId || !moduloId) {
      setRegisterError(
        "Empresa ou módulo de governança não identificado na sessão."
      );
      return;
    }

    const selectedDocumentos = documentosCatalogo.filter((doc) =>
      registerForm.documentosExigidosIds.includes(doc.id)
    );

    const statusCadastralCalculado = deriveStatusCadastralFromDocumentos(
      selectedDocumentos,
      registerForm.statusCadastral
    );

    setRegisterSaving(true);

    try {
      const payload = {
        empresa_id: empresaId,
        modulo_id: moduloId,

        tipoCanal: registerForm.tipoCanal,
        tipo_canal: registerForm.tipoCanal,

        clienteNome,
        clienteRazaoSocial: registerForm.clienteRazaoSocial,
        clienteCnpj: registerForm.clienteCnpj,
        codigoInterno: registerForm.codigoInterno,

        portalNome:
          registerForm.tipoCanal === "portal"
            ? portalNome
            : registerForm.portalNome || "Envio por e-mail",
        portalUrl:
          registerForm.tipoCanal === "portal" ? registerForm.portalUrl : "",
        usuarioReferencia:
          registerForm.tipoCanal === "portal"
            ? registerForm.usuarioReferencia
            : "",

        emailsDestino:
          registerForm.tipoCanal === "email" ? registerForm.emailsDestino : "",
        assuntoPadraoEmail:
          registerForm.tipoCanal === "email"
            ? registerForm.assuntoPadraoEmail
            : "",
        procedimentoEnvio:
          registerForm.tipoCanal === "email"
            ? registerForm.procedimentoEnvio
            : "",

        areaResponsavel: registerForm.areaResponsavel,
        responsavelNome: registerForm.responsavelNome,

        statusCadastral: statusCadastralCalculado,
        observacao: registerForm.observacao,

        documentosExigidosIds: registerForm.documentosExigidosIds,
        documentoModuloId: moduloDocumentosId,
      };

      const response = await fetch(API_PORTAIS_CLIENTES_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...requestHeaders,
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = (await response
        .json()
        .catch(() => null)) as ApiPostPortalClienteResponse | null;

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.erro ||
            `Falha ao registrar canal do cliente. Status HTTP ${response.status}.`
        );
      }

      const novoItemRaw = data.portal || data.item;
      const novoItem = novoItemRaw ? normalizePortalFromApi(novoItemRaw) : null;

      if (novoItem) {
        upsertItemNaLista(novoItem);
      } else {
        setReloadKey((prev) => prev + 1);
      }

      setRegisterOpen(false);
      setRegisterForm(initialRegisterCanalForm);
    } catch (error: any) {
      setRegisterError(
        error?.message || "Falha ao registrar canal do cliente."
      );
    } finally {
      setRegisterSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <TitleButtons
        fullBleedLine
        title={`Portais / E-mails de Clientes - ${getSectionTitle(
          activeSection
        )}`}
      />

      <div style={styles.content}>
        <section style={styles.navigationBar}>
          <nav style={styles.tabs}>
            {sections.map((item) => {
              const active = item.key === activeSection;

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => navigate(item.path)}
                  style={{
                    ...styles.tabButton,
                    ...(active
                      ? {
                          background: toRgba(activeDepartmentColor, 0.08),
                          borderColor: activeDepartmentColor,
                          color: activeDepartmentColor,
                        }
                      : {}),
                  }}
                >
                  <span style={styles.tabIcon}>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div style={styles.navActions}>
            <button
              type="button"
              style={styles.refreshButton}
              onClick={() => {
                setReloadKey((prev) => prev + 1);
                loadCanais();
              }}
              disabled={loading}
            >
              {loading ? <Loader2 size={14} /> : <Clock3 size={14} />}
              Atualizar
            </button>

            <button
              type="button"
              style={{
                ...styles.registerButton,
                background: activeDepartmentColor,
                borderColor: activeDepartmentColor,
              }}
              onClick={openRegisterOverlay}
            >
              <Plus size={14} />
              Registrar canal
            </button>
          </div>
        </section>

        

        

        {pageError && (
          <section style={styles.pageError}>
            <AlertTriangle size={15} />
            <span>{pageError}</span>
          </section>
        )}

        <section style={styles.tablePanel}>
          <div style={styles.tableHeader}>
            <div>
              <h2 style={styles.tableTitle}>Canais documentais monitorados</h2>
              <span style={styles.tableHint}>
                {loading
                  ? "Carregando registros..."
                  : `${filteredItems.length} ${plural(
                      filteredItems.length,
                      "registro",
                      "registros"
                    )}${
                      activeSection !== "visao-geral"
                        ? ` em ${getSectionTitle(activeSection).toLowerCase()}`
                        : ""
                    }`}
              </span>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Cliente</th>
                  <th style={styles.th}>Tipo</th>
                  <th style={styles.th}>Canal / Destino</th>
                  <th style={styles.th}>Status cadastral</th>
                  <th style={styles.th}>Atualizados</th>
                  <th style={styles.th}>Pendentes</th>
                  <th style={styles.th}>Vencidos</th>
                  <th style={styles.th}>Validação</th>
                  <th style={styles.th}>Responsável</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={9} style={styles.emptyCell}>
                      <span style={styles.emptyStateInline}>
                        <Loader2 size={17} />
                        Carregando canais...
                      </span>
                    </td>
                  </tr>
                )}

                {!loading && filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={9} style={styles.emptyCell}>
                      Nenhum canal de cliente encontrado para os filtros atuais.
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredItems.map((item) => (
                    <CanalRow
                      key={item.id}
                      item={item}
                      departmentColor={activeDepartmentColor}
                      onDoubleClick={() => openDetailOverlay(item)}
                    />
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <RegisterCanalOverlay
        open={registerOpen}
        form={registerForm}
        documentosCatalogo={documentosCatalogo}
        documentosLoading={documentosLoading}
        documentosError={documentosError}
        departmentColor={activeDepartmentColor}
        saving={registerSaving}
        error={registerError}
        onClose={closeRegisterOverlay}
        onChange={updateRegisterForm}
        onAddDocumento={handleAddDocumentoExigido}
        onRemoveDocumento={handleRemoveDocumentoExigido}
        onSubmit={handleRegisterCanal}
      />

      <CanalDetailOverlay
        open={detailOpen}
        item={detailItem}
        loading={detailLoading}
        saving={detailSaving}
        error={detailError}
        editMode={detailEditMode}
        editForm={detailForm}
        documentosCatalogo={documentosCatalogo}
        documentosLoading={documentosLoading}
        documentosError={documentosError}
        departmentColor={activeDepartmentColor}
        onClose={closeDetailOverlay}
        onStartEdit={startDetailEdit}
        onCancelEdit={cancelDetailEdit}
        onSaveEdit={handleSaveDetailCanal}
        onChange={updateDetailForm}
        onAddDocumento={handleAddDocumentoDetail}
        onRemoveDocumento={handleRemoveDocumentoDetail}
      />
    </div>
  );
};



const CanalRow: React.FC<{
  item: PortalCliente;
  departmentColor: string;
  onDoubleClick?: () => void;
}> = ({ item, departmentColor, onDoubleClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const statusVisual = getStatusCadastralVisual(item.statusCadastral);

  const tipoIcon =
    item.tipoCanal === "email" ? <Mail size={13} /> : <Globe2 size={13} />;

  const canalPrincipal = getCanalPrincipalLabel(item);
  const canalSecundario = getCanalSecundarioLabel(item);

  const rowStyle: React.CSSProperties = {
    cursor: onDoubleClick ? "pointer" : "default",
    background: isHovered ? toRgba(departmentColor, 0.045) : "#ffffff",
    transition: "background 0.14s ease",
  };

  const tdStyle: React.CSSProperties = {
    ...styles.td,
    borderTop: `1px solid ${
      isHovered ? toRgba(departmentColor, 0.22) : "#f0f2f4"
    }`,
  };

  return (
    <tr
      style={rowStyle}
      onDoubleClick={onDoubleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title="Duplo clique para abrir os detalhes do canal"
    >
      <td style={tdStyle}>
        <div style={styles.clientCell}>
          <span
            style={{
              ...styles.clientIcon,
              background: isHovered ? toRgba(departmentColor, 0.08) : "#f8fafc",
              borderColor: isHovered
                ? toRgba(departmentColor, 0.28)
                : "#dbe4ef",
              color: departmentColor,
            }}
          >
            <Building2 size={14} />
          </span>

          <div style={styles.clientInfo}>
            <div style={styles.clientName}>{item.clienteNome}</div>
            <div style={styles.clientMeta}>
              {item.clienteCnpj || "CNPJ não informado"}
              {item.codigoInterno ? ` · ${item.codigoInterno}` : ""}
            </div>
          </div>
        </div>
      </td>

      <td style={tdStyle}>
        <span
          style={{
            ...styles.badge,
            background: "#ffffff",
            color: "#334155",
            borderColor: "#cbd5e1",
          }}
        >
          {tipoIcon}
          {item.tipoCanal === "email" ? "E-mail" : "Portal"}
        </span>
      </td>

      <td style={tdStyle}>
        <div style={styles.portalName}>{canalPrincipal}</div>
        <div style={styles.portalUrl}>{canalSecundario}</div>
      </td>

      <td style={tdStyle}>
        <span
          style={{
            ...styles.badge,
            background: statusVisual.background,
            color: statusVisual.color,
            borderColor: statusVisual.border,
          }}
        >
          {statusVisual.icon}
          {getStatusCadastralLabel(item.statusCadastral)}
        </span>
      </td>

      <td style={tdStyle}>
        <strong style={styles.metricOk}>{item.documentosAtualizados}</strong>
        <div style={styles.clientMeta}>
          de {item.documentosExigidos} exigido(s)
        </div>
      </td>

      <td style={tdStyle}>
        <strong
          style={{
            ...styles.metricWarning,
            color: item.documentosPendentes > 0 ? "#9a3412" : "#64748b",
          }}
        >
          {item.documentosPendentes}
        </strong>
      </td>

      <td style={tdStyle}>
        <strong
          style={{
            ...styles.metricDanger,
            color: item.documentosVencidos > 0 ? "#991b1b" : "#64748b",
          }}
        >
          {item.documentosVencidos}
        </strong>
      </td>

      <td style={tdStyle}>
        <strong
          style={{
            ...styles.metricPurple,
            color:
              item.documentosAguardandoValidacao > 0 ? "#6d28d9" : "#64748b",
          }}
        >
          {item.documentosAguardandoValidacao}
        </strong>
      </td>

      <td style={tdStyle}>
        <div style={styles.responsavelText}>
          {item.responsavelNome || "Não informado"}
        </div>
        <div style={styles.clientMeta}>
          {item.areaResponsavel || "Governança"}
        </div>
      </td>
    </tr>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100%",
    background: "#FFFFFF",
    color: "#111827",
    fontFamily:
      "Inter, Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  },

  content: {
    padding: "14px 18px 28px",
  },

  navigationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },

  tabs: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    flexWrap: "wrap",
  },

  tabButton: {
    height: 32,
    padding: "0 11px",
    border: "1px solid #dbe4ef",
    background: "#ffffff",
    color: "#475569",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "inherit",
  },

  tabIcon: {
    display: "inline-flex",
    alignItems: "center",
  },

  navActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  refreshButton: {
    height: 32,
    padding: "0 11px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "inherit",
  },

  registerButton: {
    height: 32,
    padding: "0 12px",
    border: "1px solid",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "inherit",
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: 9,
    marginBottom: 12,
  },

  summaryCard: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    padding: "10px 11px",
    minHeight: 62,
  },

  summaryLabel: {
    display: "block",
    fontSize: 10.5,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    marginBottom: 5,
  },

  summaryValue: {
    display: "block",
    fontSize: 20,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1,
  },

  filtersPanel: {
    display: "grid",
    gridTemplateColumns: "1.3fr 1.5fr 0.7fr 0.9fr 0.9fr auto",
    alignItems: "end",
    gap: 9,
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    padding: 10,
    marginBottom: 12,
  },

  filterField: {
    minWidth: 0,
  },

  filterFieldSmall: {
    minWidth: 0,
  },

  filterLabel: {
    display: "block",
    marginBottom: 4,
    color: "#475569",
    fontSize: 10.8,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  filterInput: {
    width: "100%",
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 9px",
    fontSize: 12,
    fontWeight: 700,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  clearFiltersButton: {
    height: 32,
    padding: "0 11px",
    border: "1px solid #cbd5e1",
    background: "#f8fafc",
    color: "#334155",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  pageError: {
    minHeight: 34,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    padding: "8px 10px",
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 800,
  },

  tablePanel: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    overflow: "hidden",
  },

  tableHeader: {
    minHeight: 50,
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  tableTitle: {
    margin: 0,
    color: "#111827",
    fontSize: 14,
    fontWeight: 950,
  },

  tableHint: {
    display: "block",
    marginTop: 2,
    color: "#64748b",
    fontSize: 11.5,
    fontWeight: 750,
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: 1180,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  th: {
    height: 34,
    padding: "0 10px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
    color: "#475569",
    fontSize: 10.8,
    fontWeight: 950,
    textAlign: "left",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  tr: {
    height: 58,
    borderBottom: "1px solid #eef2f7",
    transition: "background 140ms ease, box-shadow 140ms ease",
    cursor: "default",
  },

  td: {
    padding: "8px 10px",
    color: "#334155",
    fontSize: 12,
    fontWeight: 700,
    verticalAlign: "middle",
    overflow: "hidden",
  },

  clientCell: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    minWidth: 0,
  },

  clientIcon: {
    width: 30,
    height: 30,
    border: "1px solid #dbe4ef",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
  },

  clientInfo: {
    minWidth: 0,
  },

  clientName: {
    color: "#111827",
    fontSize: 12.5,
    fontWeight: 950,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  clientMeta: {
    marginTop: 2,
    color: "#64748b",
    fontSize: 11.2,
    fontWeight: 750,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  portalName: {
    color: "#111827",
    fontSize: 12.5,
    fontWeight: 900,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  portalUrl: {
    marginTop: 2,
    color: "#64748b",
    fontSize: 11.2,
    fontWeight: 750,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  responsavelText: {
    color: "#111827",
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  metricOk: {
    color: "#166534",
    fontSize: 13,
    fontWeight: 950,
  },

  metricWarning: {
    fontSize: 13,
    fontWeight: 950,
  },

  metricDanger: {
    fontSize: 13,
    fontWeight: 950,
  },

  metricPurple: {
    fontSize: 13,
    fontWeight: 950,
  },

  badge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  emptyCell: {
    padding: 22,
    color: "#64748b",
    fontSize: 12.5,
    fontWeight: 800,
    textAlign: "center",
  },

  emptyStateInline: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },

  overlayBackdrop: {
    position: "fixed",
    top: HEADER_HEIGHT,
    left: LEFTBAR_WIDTH,
    right: 0,
    bottom: 0,
    zIndex: 1190,
    background:
      "radial-gradient(circle at top left, rgba(47, 63, 89, 0.10), transparent 34%), rgba(15, 23, 42, 0.28)",
    backdropFilter: "blur(3px)",
    WebkitBackdropFilter: "blur(3px)",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "flex-end",
    padding: 0,
    boxSizing: "border-box",
  },

  overlayPanel: {
    width: `min(1040px, calc(100vw - ${LEFTBAR_WIDTH}px))`,
    height: `calc(100vh - ${HEADER_HEIGHT}px)`,
    background: "#ffffff",
    borderLeft: "1px solid #dfe3e8",
    boxShadow: "-18px 0 46px rgba(15, 23, 42, 0.22)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
  },

  detailPanel: {
    width: `min(1120px, calc(100vw - ${LEFTBAR_WIDTH}px))`,
    height: `calc(100vh - ${HEADER_HEIGHT}px)`,
    background: "#ffffff",
    borderLeft: "1px solid #dfe3e8",
    boxShadow: "-18px 0 46px rgba(15, 23, 42, 0.22)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
  },

  overlayHeader: {
    padding: "16px 18px 14px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexShrink: 0,
  },

  overlayEyebrow: {
    color: "#64748b",
    fontSize: 10.8,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 4,
  },

  overlayTitle: {
    margin: 0,
    color: "#111827",
    fontSize: 18,
    fontWeight: 950,
    lineHeight: 1.15,
  },

  overlaySubtitle: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
    maxWidth: 780,
  },

  overlayCloseButton: {
    width: 32,
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  overlayError: {
    margin: "12px 18px 0",
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    padding: "8px 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 800,
  },

  overlayErrorInline: {
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    padding: "8px 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 800,
  },

  formGrid: {
    padding: 18,
    overflowY: "auto",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px 14px",
  },

  formSectionTitle: {
    gridColumn: "1 / -1",
    marginTop: 2,
    paddingTop: 8,
    borderTop: "1px solid #eef2f7",
    display: "flex",
    alignItems: "center",
    gap: 7,
    color: "#111827",
    fontSize: 12.5,
    fontWeight: 950,
  },

  formField: {
    minWidth: 0,
  },

  formFieldWide: {
    gridColumn: "1 / -1",
    minWidth: 0,
  },

  formLabel: {
    display: "block",
    marginBottom: 5,
    color: "#475569",
    fontSize: 10.8,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  formInput: {
    width: "100%",
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 10px",
    fontSize: 12.2,
    fontWeight: 750,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  formTextarea: {
    width: "100%",
    minHeight: 74,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "9px 10px",
    fontSize: 12.2,
    fontWeight: 750,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  formHelpText: {
    display: "block",
    marginTop: 4,
    color: "#64748b",
    fontSize: 11,
    fontWeight: 700,
  },

  segmentedControl: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  segmentedButton: {
    height: 34,
    padding: "0 12px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontFamily: "inherit",
  },

  docSelectShell: {
    position: "relative",
  },

  docSelectButton: {
    width: "100%",
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "0 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    fontFamily: "inherit",
  },

  docSelectButtonText: {
    fontSize: 12.2,
    fontWeight: 800,
  },

  docDropdown: {
    position: "absolute",
    zIndex: 6,
    left: 0,
    right: 0,
    top: 38,
    maxHeight: 290,
    overflowY: "auto",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    boxShadow: "0 14px 36px rgba(15, 23, 42, 0.16)",
  },

  docSearchBox: {
    height: 38,
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "0 9px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
  },

  docSearchInput: {
    width: "100%",
    border: 0,
    outline: "none",
    background: "transparent",
    color: "#111827",
    fontSize: 12,
    fontWeight: 750,
    fontFamily: "inherit",
  },

  docDropdownError: {
    padding: 10,
    color: "#991b1b",
    background: "#fef2f2",
    fontSize: 12,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    gap: 7,
  },

  docDropdownEmpty: {
    padding: 12,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
  },

  docOption: {
    width: "100%",
    padding: "9px 10px",
    border: 0,
    borderBottom: "1px solid #eef2f7",
    background: "#ffffff",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "inherit",
  },

  docOptionMain: {
    display: "block",
    color: "#111827",
    fontSize: 12.2,
    fontWeight: 900,
  },

  docOptionMeta: {
    display: "block",
    marginTop: 2,
    color: "#64748b",
    fontSize: 11,
    fontWeight: 700,
  },

  selectedDocsListPanel: {
    marginTop: 10,
    border: "1px solid #dfe3e8",
    background: "#ffffff",
  },

  selectedDocsListHeader: {
    minHeight: 42,
    padding: "8px 10px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
  },

  selectedDocsTitle: {
    display: "block",
    color: "#111827",
    fontSize: 12.5,
    fontWeight: 950,
  },

  selectedDocsHint: {
    display: "block",
    marginTop: 2,
    color: "#64748b",
    fontSize: 11.2,
    fontWeight: 750,
  },

  selectedDocsEmpty: {
    padding: 13,
    color: "#64748b",
    fontSize: 12.2,
    fontWeight: 750,
  },

  selectedDocsTableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  selectedDocsTable: {
    width: "100%",
    minWidth: 760,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  selectedDocsTh: {
    height: 32,
    padding: "0 9px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
    color: "#475569",
    fontSize: 10.5,
    fontWeight: 950,
    textAlign: "left",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  selectedDocsThAction: {
    width: 70,
    height: 32,
    padding: "0 9px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
    color: "#475569",
    fontSize: 10.5,
    fontWeight: 950,
    textAlign: "center",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  selectedDocsTr: {
    borderBottom: "1px solid #eef2f7",
  },

  selectedDocsTd: {
    padding: "8px 9px",
    color: "#334155",
    fontSize: 11.7,
    fontWeight: 750,
    verticalAlign: "middle",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  selectedDocsTdAction: {
    padding: "8px 9px",
    textAlign: "center",
  },

  selectedDocName: {
    color: "#111827",
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  selectedDocMeta: {
    marginTop: 2,
    color: "#64748b",
    fontSize: 10.8,
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  docStatusPill: {
    minHeight: 21,
    padding: "0 7px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 10.8,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  removeDocButton: {
    width: 28,
    height: 28,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  documentRuleWarning: {
    marginTop: 10,
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    color: "#9a3412",
    padding: "8px 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 800,
  },

  detailBody: {
    padding: 18,
    overflowY: "auto",
    display: "grid",
    gap: 12,
  },

  detailSummaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: 8,
  },

  detailSummaryCard: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    padding: "9px 10px",
    minHeight: 58,
  },

  detailSummaryLabel: {
    display: "block",
    marginBottom: 5,
    color: "#64748b",
    fontSize: 10.5,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  detailSummaryValue: {
    display: "block",
    color: "#111827",
    fontSize: 18,
    fontWeight: 950,
    lineHeight: 1,
  },

  detailInfoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
  },

  detailInfoItem: {
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    padding: "9px 10px",
    minWidth: 0,
  },

  detailInfoLabel: {
    display: "block",
    marginBottom: 2,
    color: "#64748b",
    fontSize: 10.8,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  detailInfoValue: {
    display: "block",
    color: "#111827",
    fontSize: 12.2,
    fontWeight: 850,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  detailObservation: {
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    padding: "9px 10px",
  },

  detailObservationText: {
    margin: 0,
    color: "#334155",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  detailDocsPanel: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    overflow: "hidden",
  },

  detailDocsHeader: {
    minHeight: 44,
    padding: "9px 11px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  detailDocsTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 950,
    color: "#111827",
  },

  detailDocsHint: {
    display: "block",
    marginTop: 2,
    fontSize: 11.2,
    fontWeight: 750,
    color: "#64748b",
  },

  detailDocsTable: {
    width: "100%",
    minWidth: 980,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  overlayFooter: {
    padding: "12px 18px 16px",
    borderTop: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexShrink: 0,
  },

  cancelButton: {
    height: 34,
    padding: "0 13px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  saveButton: {
    height: 34,
    padding: "0 14px",
    border: "1px solid",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  },
};

export default GovernancaPortaisClientesLayout;
